import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tabs,
  Tab,
  SelectChangeEvent,
  Alert,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DescriptionIcon from '@mui/icons-material/Description';
import NotesIcon from '@mui/icons-material/Notes';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import ViewListIcon from '@mui/icons-material/ViewList';
import movMonedaService from '../../services/movMonedaService';
import tipoMonedaService, { TipoMoneda } from '../../services/tipoMonedaService';
import transporteService, { Transporte } from '../../services/transporteService';
import productoService from '../../services/productoService';
import ubicacionService from '../../services/ubicacionService';
import { IUbicacion } from '../../interfaces/Ubicacion';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatCurrency, normalizeItemNotas, parseLocalDate } from '../../utils/formatters';
import { PedidoVenta, PedidoVentaDetalle } from '../../services/pedidoVentaService';
import { catalogosService } from '../../services/catalogosService';
import api from '../../services/api';
import { getProductoFallbackImage, getProductoImageBySize } from '../../utils/productImage';
import inventarioInformes from '../../services/inventarioInformes';
import VendedorSearch from '../VendedorSearch';
import ListaPrecioSearch from '../ListaPrecioSearch';
import vendedorService, { Vendedor } from '../../services/vendedorService';
import listaPrecioService, { ListaPrecio } from '../../services/listaPrecioService';
import { useSnackbar } from 'notistack';
import articuloXListaPrecioService, { ArticuloXListaPrecio } from '../../services/articuloXListaPrecioService';
import { obtenerPrecioBaseProducto, resolverPrecioDesdeLista } from '../../utils/precioListaPedido';
import ventasSeguimientoService, {
  VentaPendienteMaster,
  VentaHiloComprobante,
  formatComprobanteVentaMasterDisplay,
  formatComprobanteVentaHiloLine,
  cantidadSugeridaDesdePendienteVenta,
} from '../../services/ventasSeguimientoService';

const VENTA_ITEM_NOTAS_MAX = 500;
type VistaProductos = 'cuadrados' | 'tarjetas' | 'renglones';
const VISTA_PRODUCTOS_STORAGE_KEY = 'ventas.vistaProductos';

const getVistaProductosInicial = (): VistaProductos => {
  try {
    const saved = localStorage.getItem(VISTA_PRODUCTOS_STORAGE_KEY);
    if (saved === 'cuadrados' || saved === 'tarjetas' || saved === 'renglones') {
      return saved;
    }
  } catch {
    // noop
  }
  return 'cuadrados';
};

/** Tipo de documento compartido para formularios de venta (pedido o cotización). */
export type FormularioVentaFormDocument = PedidoVenta & {
  ordenCompra?: string;
  transporte?: string;
  ubicacion?: string;
  formaDePago?: string;
  nroSuc?: number;
  cuit?: string;
  tipoIVA?: string;
  dni?: string;
  idCompania?: string;
};

interface SucursalVenta {
  nroSuc: number;
  nombreSucursal?: string;
}

interface Compania {
  idCompania: string;
  nombre: string;
}

export interface FormularioVentaFormProps {
  document: FormularioVentaFormDocument;
  onChange: (document: FormularioVentaFormDocument) => void;
  onClienteSearch: () => void;
  onProductoSearch: () => void;
  onSave: () => void;
  loading: boolean;
  saveButtonText?: string;
  readOnly?: boolean;
  hideFinancials?: boolean;
  tabLabelInfo?: string;
  tabLabelResumen?: string;
  ariaLabelTabs?: string;
}

const FormularioVentaForm: React.FC<FormularioVentaFormProps> = ({
  document,
  onChange,
  onClienteSearch,
  onProductoSearch,
  onSave,
  loading,
  saveButtonText = 'Guardar Pedido', // Valor por defecto para mantener compatibilidad
  readOnly = false,
  hideFinancials = false,
  tabLabelInfo = 'Información del Pedido',
  tabLabelResumen = 'Resumen del Pedido',
  ariaLabelTabs = 'pestañas de pedido',
}) => {
  const isCotizacion = tabLabelInfo?.toLowerCase().includes('cotización');
  const totalLabel = isCotizacion ? 'Total de la Cotización' : 'Total del Pedido';
  const eliminarLabel = isCotizacion ? 'Eliminar de la cotización' : 'Eliminar del pedido';

  const [transportes, setTransportes] = useState<Transporte[]>([]);
  const [ubicaciones, setUbicaciones] = useState<IUbicacion[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [productos, setProductos] = useState<any[]>([]);
  const [prodSearch, setProdSearch] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [dialogQty, setDialogQty] = useState<number>(1);
  const [dialogDiscount, setDialogDiscount] = useState<number>(0);
  const [dialogNotas, setDialogNotas] = useState<string>('');
  const [monedaInfoDialogOpen, setMonedaInfoDialogOpen] = useState(false);
  const [subtotalInfoDialogOpen, setSubtotalInfoDialogOpen] = useState(false);
  const [selectedDetalleForInfo, setSelectedDetalleForInfo] = useState<PedidoVentaDetalle | null>(null);
  const [ramoCache, setRamoCache] = useState<Record<string, string>>({});
  const [ramoEditaPrecioCache, setRamoEditaPrecioCache] = useState<Record<string, boolean>>({});
  const [dialogPrecioLista, setDialogPrecioLista] = useState<number | null>(null);
  const [bonif1, setBonif1] = useState<number>(Number(document.porcDesc ?? 0) || 0);
  const [bonif2, setBonif2] = useState<number>(Number(document.porcDesc2 ?? 0) || 0);
  const [bonif3, setBonif3] = useState<number>(Number(document.porcDesc3 ?? 0) || 0);
  const [formaDePagoDialogOpen, setFormaDePagoDialogOpen] = useState(false);
  const [ubicacionDialogOpen, setUbicacionDialogOpen] = useState(false);
  const [ubicacionSearchTerm, setUbicacionSearchTerm] = useState('');
  const [vistaProductos, setVistaProductos] = useState<VistaProductos>(() => getVistaProductosInicial());
  const [productosPage, setProductosPage] = useState(0);
  const [productosRowsPerPage] = useState(20);
  const [productosTotalPages, setProductosTotalPages] = useState(1);
  const [cotizacionesCache, setCotizacionesCache] = useState<Record<number, number | null>>({});
  const [cotizacionesCargando, setCotizacionesCargando] = useState<Record<number, boolean>>({});
  const [tiposMonedaCache, setTiposMonedaCache] = useState<Record<number, TipoMoneda>>({});
  const [tiposMonedaCargando, setTiposMonedaCargando] = useState(false);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [productosDataCache, setProductosDataCache] = useState<Record<string, any>>({});
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [stockPorUbicacion, setStockPorUbicacion] = useState<Record<string, number>>({});
  const [stockInfoAnchorEl, setStockInfoAnchorEl] = useState<HTMLElement | null>(null);
  const [stockInfoProducto, setStockInfoProducto] = useState<{ codigoArticulo: string; descripcion: string; stockTotal?: number } | null>(null);
  const [stockInfoData, setStockInfoData] = useState<{ codigoArticulo: string; descripcion: string; nroUbicacion: number; nombreUbicacion: string; cantidadDeposito: number; cantidadAsignada: number; disponibleDeposito: number }[]>([]);
  const [stockInfoLoading, setStockInfoLoading] = useState(false);
  const [openVendedorSearch, setOpenVendedorSearch] = useState(false);
  const [openListaPrecioSearch, setOpenListaPrecioSearch] = useState(false);
  const [openPendientesMaster, setOpenPendientesMaster] = useState(false);
  const [loadingPendientesMaster, setLoadingPendientesMaster] = useState(false);
  const [pendientesMasterRows, setPendientesMasterRows] = useState<VentaPendienteMaster[]>([]);
  const [cantidadesPendientesMaster, setCantidadesPendientesMaster] = useState<Record<string, string>>({});
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);
  const [pendienteHiloItems, setPendienteHiloItems] = useState<VentaHiloComprobante[]>([]);
  const [loadingPendienteHilo, setLoadingPendienteHilo] = useState(false);
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState<Pick<Vendedor, 'nroVendedor' | 'nombreVendedor'> | null>(null);
  const [listaPrecioSeleccionada, setListaPrecioSeleccionada] = useState<Pick<ListaPrecio, 'codigoLista' | 'descripcion'> | null>(null);
  const [articulosListaCache, setArticulosListaCache] = useState<Record<string, ArticuloXListaPrecio | null>>({});
  const [catalogoPrecioCache, setCatalogoPrecioCache] = useState<Record<string, { precioBase: number; codigoMoneda: number | null; origen: 'lista' | 'stock' }>>({});
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const { enqueueSnackbar } = useSnackbar();
  const nroSucursalSeleccionada = document.nroSuc;
  const clienteSeleccionado = document.nroCliente && document.nroCliente > 0;
  const clampPercent = (value: number) => Math.max(0, Math.min(100, value));
  const getPendienteMasterRowKey = (p: VentaPendienteMaster) => `${p.nroFormularioMaster}-${p.nroItemMaster}`;
  const getPendienteMasterOriginal = (p: VentaPendienteMaster) =>
    hideFinancials ? Number(p.pendienteEntregar) || 0 : Number(p.pendienteFacturar) || 0;
  const getPendienteMasterCantidadSugerida = (p: VentaPendienteMaster) => {
    const original = getPendienteMasterOriginal(p);
    return original > 0 ? original : cantidadSugeridaDesdePendienteVenta(p);
  };
  const parsePendienteMasterCantidadEditable = (p: VentaPendienteMaster): number => {
    const key = getPendienteMasterRowKey(p);
    const raw = cantidadesPendientesMaster[key];
    if (raw === undefined) {
      return getPendienteMasterCantidadSugerida(p);
    }
    const value = Number(raw.toString().replace(',', '.'));
    return Number.isFinite(value) ? value : 0;
  };
  const esConsumidorFinal = (() => {
    const tipo = (document.tipoIVA || '').toString().toUpperCase();
    return tipo === 'C' || tipo === 'CF';
  })();

  useEffect(() => {
    try {
      localStorage.setItem(VISTA_PRODUCTOS_STORAGE_KEY, vistaProductos);
    } catch {
      // noop
    }
  }, [vistaProductos]);

  // Función helper para obtener el IVA del producto según el tipo de IVA del cliente
  const getIvaRate = (producto: any): number => {
    const tipoIVA = document.tipoIVA || '';
    const calculaIva = producto?.calculaIva;
    
    // Si el producto no calcula IVA, retornar 0
    if (calculaIva !== 'S' && calculaIva !== 's') {
      return 0;
    }

    // Mapear tipo de IVA del cliente a los campos de IVA del producto
    let ivaValue = 0;
    switch (tipoIVA) {
      case 'I': // Responsable Inscripto
        ivaValue = producto?.ivaRI ?? 0;
        break;
      case 'N': // No Categorizado
        ivaValue = producto?.ivaRN ?? 0;
        break;
      case 'E': // Exento
        ivaValue = producto?.ivaEX ?? 0;
        break;
      case 'M': // Monotributo
        ivaValue = producto?.ivaMT ?? 0;
        break;
      case 'C': // Consumidor Final
      case 'CF': // Consumidor Final (compatibilidad)
        ivaValue = producto?.ivaCF ?? 0;
        break;
      default:
        // Por defecto usar ivaRI si no hay tipo de IVA especificado
        ivaValue = producto?.ivaRI ?? 21;
    }

    // Convertir el porcentaje a decimal (ej: 21 -> 0.21)
    return Number(ivaValue) / 100;
  };

  // Función helper para obtener el multiplicador de IVA (1 + ivaRate)
  const getIvaMultiplier = (producto: any): number => {
    const ivaRate = getIvaRate(producto);
    return 1 + ivaRate;
  };

  // Función para obtener el IVA rate de un producto por código
  const getIvaRateByCodigo = (codigoArticulo: string): number => {
    const producto = productosDataCache[codigoArticulo];
    if (!producto) {
      // Si no tenemos los datos del producto, usar 21% por defecto
      return 0.21;
    }
    return getIvaRate(producto);
  };

  // Función para obtener el multiplicador de IVA por código
  const getIvaMultiplierByCodigo = (codigoArticulo: string): number => {
    const ivaRate = getIvaRateByCodigo(codigoArticulo);
    return 1 + ivaRate;
  };

  // Total calculado en tiempo real (incluye recálculo por tipo IVA, bonificaciones, etc.)
  const totalCalculado = useMemo(() => {
    if (!document.detalles?.length) return document.importeTotal ?? 0;
    const sumaSubFinalSinIVA = document.detalles.reduce((sum, d) => {
      const sb = (d.cantidad || 0) * (d.precioUnitario || 0);
      const sf = sb * (1 - Number(d.descuento || 0) / 100);
      return sum + sf;
    }, 0);
    const totalConBonifYDescuentos = sumaSubFinalSinIVA *
      (1 - (bonif1 || 0) / 100) *
      (1 - (bonif2 || 0) / 100) *
      (1 - (bonif3 || 0) / 100);
    let montoIVA = 0;
    document.detalles.forEach((d) => {
      const sb = (d.cantidad || 0) * (d.precioUnitario || 0);
      const subConDescuento = sb * (1 - Number(d.descuento || 0) / 100);
      const proporcion = sumaSubFinalSinIVA > 0 ? subConDescuento / sumaSubFinalSinIVA : 0;
      const subConBonif = totalConBonifYDescuentos * proporcion;
      const ivaRate = getIvaRateByCodigo(d.codigoArticulo || '');
      montoIVA += subConBonif * ivaRate;
    });
    return totalConBonifYDescuentos + montoIVA;
  }, [document.detalles, document.tipoIVA, document.importeTotal, bonif1, bonif2, bonif3, productosDataCache]);

  // Función helper para obtener el codigoMoneda de un producto
  const getCodigoMonedaProducto = (producto: any): number | null => {
    if (!producto) return null;
    // Intentar obtener codigoMoneda desde diferentes campos posibles
    const codigoMoneda = producto.codigoMoneda ?? producto.codMoneda ?? producto.tipoMoneda ?? producto.tipoMonedaArticulo;
    const codigoMonedaNum = Number(codigoMoneda);
    // Si es 0 o null, significa ARS (no necesita cotización)
    if (!codigoMoneda || codigoMonedaNum === 0 || !Number.isFinite(codigoMonedaNum)) {
      return null;
    }
    return codigoMonedaNum;
  };

  const withCodigoMoneda = (producto: any, codigoMoneda: number | null): any => {
    if (!codigoMoneda || codigoMoneda === 0) {
      return producto;
    }
    return {
      ...producto,
      codigoMoneda,
      codMoneda: codigoMoneda,
      tipoMoneda: codigoMoneda,
      tipoMonedaArticulo: codigoMoneda,
    };
  };

  const obtenerArticuloLista = async (nroLista: string, codigoArticulo: string): Promise<ArticuloXListaPrecio | null> => {
    const key = `${nroLista}|${codigoArticulo}`;
    if (Object.prototype.hasOwnProperty.call(articulosListaCache, key)) {
      return articulosListaCache[key];
    }

    try {
      const registro = await articuloXListaPrecioService.getArticuloXListaPrecio(nroLista, codigoArticulo);
      setArticulosListaCache(prev => ({ ...prev, [key]: registro }));
      return registro;
    } catch (error: any) {
      const status = Number(error?.response?.status ?? 0);
      if (status === 404) {
        setArticulosListaCache(prev => ({ ...prev, [key]: null }));
        return null;
      }
      return null;
    }
  };

  // Función helper para obtener la cotización según el codigoMoneda del producto
  const obtenerCotizacionPorProducto = async (producto: any): Promise<number | null> => {
    const codigoMoneda = getCodigoMonedaProducto(producto);
    
    // Si no hay codigoMoneda o es 0 (ARS), no necesita cotización
    if (!codigoMoneda || codigoMoneda === 0) {
      return null;
    }

    // Si ya tenemos la cotización en caché, devolverla
    if (cotizacionesCache.hasOwnProperty(codigoMoneda)) {
      return cotizacionesCache[codigoMoneda];
    }

    // Si ya está cargando, devolver null (se actualizará cuando termine)
    if (cotizacionesCargando[codigoMoneda]) {
      return null;
    }

    // Marcar como cargando
    setCotizacionesCargando(prev => ({ ...prev, [codigoMoneda]: true }));

    try {
      // Obtener la cotización desde el servicio
      const cotizacion = await movMonedaService.getUltimaCotizacionPorMoneda(codigoMoneda);
      
      // Guardar en caché (incluso si es null, para indicar que se intentó cargar)
      setCotizacionesCache(prev => ({ ...prev, [codigoMoneda]: cotizacion ?? null }));
      setCotizacionesCargando(prev => {
        const nuevo = { ...prev };
        delete nuevo[codigoMoneda];
        return nuevo;
      });

      return cotizacion ?? null;
    } catch (error) {
      console.error(`Error al obtener cotización para codigoMoneda ${codigoMoneda}:`, error);
      // Guardar null en caché para indicar que no hay cotización disponible
      setCotizacionesCache(prev => ({ ...prev, [codigoMoneda]: null }));
      setCotizacionesCargando(prev => {
        const nuevo = { ...prev };
        delete nuevo[codigoMoneda];
        return nuevo;
      });
      return null;
    }
  };

  // Función helper para obtener cotización de forma síncrona (desde caché)
  const getCotizacionPorProducto = (producto: any): number | null => {
    const codigoMoneda = getCodigoMonedaProducto(producto);
    if (!codigoMoneda || codigoMoneda === 0) {
      return null;
    }
    return cotizacionesCache[codigoMoneda] ?? null;
  };

  // Función helper para obtener el nombre de la moneda según su código
  const getNombreMoneda = (codigoMoneda: number | null, tipoMoneda?: number): string => {
    if (!codigoMoneda || codigoMoneda === 0) {
      return 'ARS';
    }
    
    // Si tipoMoneda es 1 o codigoMoneda es 1, mostrar USD
    if (codigoMoneda === 1 || tipoMoneda === 1) {
      return 'USD';
    }
    
    // Para otras monedas, mostrar el código
    return `MONEDA_${codigoMoneda}`;
  };

  // Función helper para obtener el símbolo de moneda según el tipoMoneda
  const getSimboloMoneda = (tipoMoneda: number | null | undefined): string => {
    if (!tipoMoneda || tipoMoneda === 0) {
      return '$'; // ARS por defecto
    }
    
    const tipoMonedaData = tiposMonedaCache[tipoMoneda];
    if (tipoMonedaData && tipoMonedaData.simbolo) {
      return tipoMonedaData.simbolo;
    }
    
    // Fallback: si es tipoMoneda 1, usar USD
    if (tipoMoneda === 1) {
      return 'USD';
    }
    
    return '$'; // Fallback a peso argentino
  };

  // Función helper para formatear precio con decimales personalizados
  const formatearPrecioConDecimales = (amount: number, simbolo: string, decimales: number = 2): string => {
    const formattedAmount = new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales
    }).format(amount);
    return `${simbolo}${formattedAmount}`;
  };

  // Función helper para formatear precio según el tipoMoneda del producto
  const formatearPrecioPorTipoMoneda = (precio: number, producto: any, precioConvertido: boolean = false, decimales: number = 2): string => {
    // Si el precio está convertido a ARS, usar símbolo de peso
    if (precioConvertido) {
      return formatearPrecioConDecimales(precio, '$', decimales);
    }
    
    // Si no está convertido, usar el símbolo del tipoMoneda original
    const tipoMoneda = producto?.tipoMoneda ?? producto?.tipoMonedaArticulo ?? 0;
    const simbolo = getSimboloMoneda(Number(tipoMoneda));
    return formatearPrecioConDecimales(precio, simbolo, decimales);
  };

  // Función helper para verificar si definitivamente NO hay cotización disponible para un codigoMoneda
  // Retorna true solo si se intentó cargar y no existe (está en caché como null)
  const noHayCotizacionDisponible = (codigoMoneda: number | null): boolean => {
    if (!codigoMoneda || codigoMoneda === 0) {
      return false; // ARS no necesita cotización
    }
    
    // Si está en caché como null, significa que se intentó cargar y no existe
    if (cotizacionesCache.hasOwnProperty(codigoMoneda) && cotizacionesCache[codigoMoneda] === null) {
      return true;
    }
    
    // Si tiene un valor en caché, hay cotización disponible
    if (cotizacionesCache[codigoMoneda] && cotizacionesCache[codigoMoneda]! > 0) {
      return false;
    }
    
    // Si no está en caché, aún no se sabe (no mostrar mensaje todavía)
    return false;
  };

  // Función helper para calcular el precio con cotización (de forma síncrona desde caché)
  // Si redondearIntermedio es false, no redondea el precio base antes de multiplicar (útil para cálculos precisos)
  const calcularPrecioConCotizacion = (producto: any, precioBase: number, redondearIntermedio: boolean = true): { precio: number; tieneCotizacion: boolean; codigoMoneda: number | null } => {
    const codigoMoneda = getCodigoMonedaProducto(producto);
    
    // Si no hay codigoMoneda o es ARS (0), retornar precio base
    if (!codigoMoneda || codigoMoneda === 0) {
      return { precio: round2(precioBase), tieneCotizacion: false, codigoMoneda: null };
    }

    // Obtener cotización desde caché
    const cotizacion = cotizacionesCache[codigoMoneda];
    
    if (cotizacion && cotizacion > 0) {
      // Para monedas extranjeras, no redondear el precio base antes de multiplicar para mantener precisión
      const precioBaseParaCalcular = redondearIntermedio ? round2(precioBase) : precioBase;
      const precioCalculado = precioBaseParaCalcular * cotizacion;
      return { precio: redondearIntermedio ? round2(precioCalculado) : precioCalculado, tieneCotizacion: true, codigoMoneda };
    }
    
    // No hay cotización disponible (ya sea porque no está en caché o porque es null)
    return { precio: round2(precioBase), tieneCotizacion: false, codigoMoneda };
  };

  const resolverPrecioCatalogo = (producto: any): { precioBase: number; precio: number; tieneCotizacion: boolean; codigoMoneda: number | null; productoParaMoneda: any; origen: 'lista' | 'stock' } => {
    const codigoArticulo = String(producto?.codigoArticulo || producto?.codigo || '').trim();
    const nroLista = String(document.nroLista || '').trim();
    const precioBaseStock = obtenerPrecioBaseProducto(producto);
    const codigoMonedaStock = getCodigoMonedaProducto(producto);
    const key = nroLista && codigoArticulo ? `${nroLista}|${codigoArticulo}` : '';

    const cachePrecio = key ? catalogoPrecioCache[key] : null;
    const precioBase = cachePrecio ? cachePrecio.precioBase : precioBaseStock;
    const codigoMoneda = cachePrecio ? cachePrecio.codigoMoneda : codigoMonedaStock;
    const origen = cachePrecio ? cachePrecio.origen : 'stock';
    const productoParaMoneda = withCodigoMoneda(producto, codigoMoneda);

    const codigoMonedaTemp = getCodigoMonedaProducto(productoParaMoneda);
    const esMonedaEspecialTemp = codigoMonedaTemp !== null && codigoMonedaTemp !== 0;
    const { precio, tieneCotizacion, codigoMoneda: codigoMonedaCalculada } = calcularPrecioConCotizacion(
      productoParaMoneda,
      precioBase,
      !esMonedaEspecialTemp,
    );

    return {
      precioBase,
      precio,
      tieneCotizacion,
      codigoMoneda: codigoMonedaCalculada,
      productoParaMoneda,
      origen,
    };
  };

  useEffect(() => {
    const nextBonif1 = clampPercent(Number(document.porcDesc ?? 0) || 0);
    const nextBonif2 = clampPercent(Number(document.porcDesc2 ?? 0) || 0);
    const nextBonif3 = clampPercent(Number(document.porcDesc3 ?? 0) || 0);

    if (nextBonif1 !== bonif1) {
      setBonif1(nextBonif1);
    }
    if (nextBonif2 !== bonif2) {
      setBonif2(nextBonif2);
    }
    if (nextBonif3 !== bonif3) {
      setBonif3(nextBonif3);
    }
  }, [document.porcDesc, document.porcDesc2, document.porcDesc3, bonif1, bonif2, bonif3]);

  const handleBonificacionChange = (index: 1 | 2 | 3, rawValue: string) => {
    const numericValue = clampPercent(Number(rawValue) || 0);
    if ((index === 1 && numericValue === bonif1) ||
        (index === 2 && numericValue === bonif2) ||
        (index === 3 && numericValue === bonif3)) {
      return;
    }

    const updatedPedido = {
      ...document,
      porcDesc: index === 1 ? numericValue : (document.porcDesc ?? bonif1 ?? 0),
      porcDesc2: index === 2 ? numericValue : (document.porcDesc2 ?? bonif2 ?? 0),
      porcDesc3: index === 3 ? numericValue : (document.porcDesc3 ?? bonif3 ?? 0),
    } as FormularioVentaFormDocument;
    onChange(updatedPedido);

    if (index === 1) {
      setBonif1(numericValue);
    } else if (index === 2) {
      setBonif2(numericValue);
    } else {
      setBonif3(numericValue);
    }
  };

  const _sucursalRecomendada = 8;
  const sucursalesOrdenadas = useMemo(() => {
    if (!sucursales.length) return [];
    return [...sucursales].sort((a, b) => a.nroSuc - b.nroSuc);
  }, [sucursales]);
  
  const formasDePago = [
    'CONTADO',
    'CTA.CTE.',
    'EFECTIVO',
    'DEVOLUCION',
    'CONTRAREEMBOLSO'
  ];

  // Filtrar ubicaciones según el término de búsqueda
  const filteredUbicaciones = ubicaciones.filter((ubicacion) => {
    const search = ubicacionSearchTerm.toLowerCase();
    return (
      ubicacion.nombreUbicacion?.toLowerCase().includes(search) ||
      String(ubicacion.nroUbicacion)?.includes(search)
    );
  });

  // Cargar tipos de moneda al inicio
  useEffect(() => {
    (async () => {
      if (tiposMonedaCargando || Object.keys(tiposMonedaCache).length > 0) {
        return; // Ya está cargando o ya están cargados
      }
      
      setTiposMonedaCargando(true);
      try {
        const tipos = await tipoMonedaService.getTiposMoneda();
        const cache: Record<number, TipoMoneda> = {};
        tipos.forEach(tipo => {
          const codigo = tipo.codigoMoneda ?? tipo.tipoMoneda;
          if (codigo !== undefined && codigo !== null) {
            cache[Number(codigo)] = tipo;
          }
        });
        setTiposMonedaCache(cache);
      } catch (error) {
        console.error('Error al cargar tipos de moneda:', error);
      } finally {
        setTiposMonedaCargando(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const lista = await transporteService.getTransportes();
        setTransportes(lista);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const cargarSucursales = async () => {
      try {
        setLoadingSucursales(true);
        const response = await api.get('/api/SucursalesVenta');
        const data = response.data;
        if (data && Array.isArray(data.items)) {
          setSucursales(data.items);
        } else if (Array.isArray(data)) {
          setSucursales(data);
        } else {
          setSucursales([]);
        }
      } catch (error) {
        console.error('Error al cargar sucursales', error);
        setSucursales([]);
      } finally {
        setLoadingSucursales(false);
      }
    };
    cargarSucursales();
  }, []);

  // Preseleccionar la primera sucursal cargada si no hay ninguna elegida
  useEffect(() => {
    if (document.nroFormulario && document.nroFormulario > 0) {
      return;
    }
    if (
      sucursales.length &&
      (!nroSucursalSeleccionada || nroSucursalSeleccionada <= 0)
    ) {
      const sucursalesOrdenadas = [...sucursales].sort((a, b) => a.nroSuc - b.nroSuc);
      const primeraSucursal = sucursalesOrdenadas[0];
      if (primeraSucursal) {
        onChange({
          ...document,
          nroSuc: primeraSucursal.nroSuc,
        } as FormularioVentaFormDocument);
      }
    }
  }, [sucursales, nroSucursalSeleccionada, onChange, document]);

  // Cargar compañías para el combo de compañía del pedido
  useEffect(() => {
    const cargarCompanias = async () => {
      try {
        const response = await api.get('/api/Compañias');
        const data = response.data;

        let items: Compania[] = [];
        if (Array.isArray(data)) {
          items = data as Compania[];
        } else if (data && Array.isArray(data.items)) {
          items = data.items as Compania[];
        }

        // Normalizar mínimos: recortar espacios de idCompania y nombre
        const normalizadas = items.map((c) => ({
          ...c,
          idCompania: String(c.idCompania || '').trim(),
          nombre: String(c.nombre || '').trim(),
        }));

        setCompanias(normalizadas);
      } catch (error) {
        console.error('Error al cargar compañías', error);
        setCompanias([]);
      }
    };

    cargarCompanias();
  }, []);

  // Preseleccionar la primera compañía cargada si no hay ninguna elegida
  useEffect(() => {
    if (document.nroFormulario && document.nroFormulario > 0) {
      return;
    }
    const idCompaniaActual = document.idCompania;
    if (!idCompaniaActual && companias.length > 0) {
      const primera = companias[0];
      if (primera && primera.idCompania) {
        onChange({
          ...document,
          idCompania: primera.idCompania,
        } as FormularioVentaFormDocument);
      }
    }
  }, [companias, document, onChange]);

  useEffect(() => {
    (async () => {
      try {
        const lista = await ubicacionService.getUbicaciones();
        setUbicaciones(Array.isArray(lista) ? lista : []);
      } catch {}
    })();
  }, []);

  // Preseleccionar la primera ubicación cargada si no hay ninguna elegida (mismo criterio que sucursal en comprobante nuevo)
  useEffect(() => {
    if (document.nroFormulario && document.nroFormulario > 0) {
      return;
    }
    const ubicacionTrim = (document.ubicacion || '').trim();
    if (ubicacionTrim) {
      return;
    }
    if (!ubicaciones.length) {
      return;
    }
    const ordenadas = [...ubicaciones].sort((a, b) => Number(a.nroUbicacion) - Number(b.nroUbicacion));
    const primera = ordenadas[0];
    if (primera == null || primera.nroUbicacion === undefined || primera.nroUbicacion === null) {
      return;
    }
    const nombre = String(primera.nombreUbicacion ?? '').trim() || `Ubicación ${primera.nroUbicacion}`;
    const valorUbicacion = `${nombre} (${primera.nroUbicacion})`;
    onChange({
      ...document,
      ubicacion: valorUbicacion,
    } as FormularioVentaFormDocument);
  }, [ubicaciones, document, onChange]);

  // Resetear a la primera página cuando cambia la búsqueda
  useEffect(() => {
    setProductosPage(0);
  }, [prodSearch]);

  useEffect(() => {
    const fetch = async () => {
      const res = await productoService.getProductos({ 
        searchTerm: prodSearch || undefined, 
        pageNumber: productosPage + 1, 
        pageSize: productosRowsPerPage 
      });
      const items = res.items || [];
      const getDesc = (p: any) => (p.descripcion || p.descripcionArticulo || '').toString();
      const sorted = [...items].sort((a, b) => getDesc(a).localeCompare(getDesc(b), 'es', { sensitivity: 'base' }));
      
      // Almacenar los productos en el cache para tener acceso a los datos de IVA
      const newCache: Record<string, any> = {};
      sorted.forEach((p: any) => {
        const codigo = p.codigoArticulo || p.codigo;
        if (codigo) {
          newCache[codigo] = p;
        }
      });
      setProductosDataCache(prev => ({ ...prev, ...newCache }));
      
      setProductos(sorted);
      setProductosTotalPages(res.totalPages || 1);

      // Precargar cotizaciones para los productos visibles que tengan codigoMoneda diferente de ARS
      const codigosMonedaUnicos = new Set<number>();
      sorted.forEach((p: any) => {
        const codigoMoneda = getCodigoMonedaProducto(p);
        if (codigoMoneda && codigoMoneda !== 0) {
          codigosMonedaUnicos.add(codigoMoneda);
        }
      });

      // Cargar cotizaciones para los códigos de moneda únicos (si no están ya en caché o cargando)
      codigosMonedaUnicos.forEach(async (codigoMoneda) => {
        if (!cotizacionesCache.hasOwnProperty(codigoMoneda) && !cotizacionesCargando[codigoMoneda]) {
          await obtenerCotizacionPorProducto({ codigoMoneda });
        }
      });
    };
    fetch();
  }, [productosPage, productosRowsPerPage, prodSearch]);

  useEffect(() => {
    const nroLista = String(document.nroLista || '').trim();
    if (!nroLista || !productos.length) {
      return;
    }

    const productosConCodigo = productos
      .map((p) => ({
        codigo: String(p?.codigoArticulo || p?.codigo || '').trim(),
        producto: p,
      }))
      .filter((x) => x.codigo !== '');

    const pendientes = productosConCodigo.filter((x) => !catalogoPrecioCache.hasOwnProperty(`${nroLista}|${x.codigo}`));
    if (!pendientes.length) {
      return;
    }

    let cancelled = false;
    const cargarPreciosListaCatalogo = async () => {
      const entries = await Promise.all(
        pendientes.map(async ({ codigo, producto }) => {
          const key = `${nroLista}|${codigo}`;
          const precioBaseStock = obtenerPrecioBaseProducto(producto);
          const codigoMonedaStock = getCodigoMonedaProducto(producto);

          try {
            const registroLista = await obtenerArticuloLista(nroLista, codigo);
            const precioResuelto = resolverPrecioDesdeLista(producto, registroLista);
            if (precioResuelto.tipo === 'sinPrecio') {
              return [
                key,
                {
                  precioBase: precioBaseStock,
                  codigoMoneda: codigoMonedaStock,
                  origen: 'stock' as const,
                },
              ] as const;
            }

            return [
              key,
              {
                precioBase: precioResuelto.precioLista,
                codigoMoneda: precioResuelto.codigoMoneda ?? codigoMonedaStock,
                origen: 'lista' as const,
              },
            ] as const;
          } catch {
            return [
              key,
              {
                precioBase: precioBaseStock,
                codigoMoneda: codigoMonedaStock,
                origen: 'stock' as const,
              },
            ] as const;
          }
        }),
      );

      if (cancelled || !entries.length) {
        return;
      }

      setCatalogoPrecioCache((prev) => {
        const next = { ...prev };
        entries.forEach(([key, value]) => {
          next[key] = value;
        });
        return next;
      });
    };

    cargarPreciosListaCatalogo();
    return () => {
      cancelled = true;
    };
  }, [productos, document.nroLista, catalogoPrecioCache]);

  // Cargar stock por ubicación cuando hay ubicación seleccionada
  useEffect(() => {
    const ubicacionStr = document.ubicacion;
    const nroUbicacionStr = ubicacionStr?.match(/\((\d+)\)/)?.[1];
    const nroUbicacion = nroUbicacionStr ? parseInt(nroUbicacionStr, 10) : null;

    if (!nroUbicacion || !productos.length) {
      setStockPorUbicacion({});
      return;
    }

    const fetchStock = async () => {
      try {
        const results = await Promise.all(
          productos.map(async (p) => {
            const codigo = p.codigoArticulo || p.codigo;
            if (!codigo) return { codigo: '', disponible: 0 };
            try {
              const res = await inventarioInformes.getStockPorDeposito({
                codigoArticulo: codigo,
                nroUbicacion,
                page: 1,
                pageSize: 10,
              });
              const data = res.data || [];
              const row = data.find((r: any) => (r.codigoArticulo || '').trim() === String(codigo).trim());
              const disponible = row ? Number(row.disponibleDeposito ?? 0) : 0;
              return { codigo, disponible };
            } catch {
              return { codigo, disponible: 0 };
            }
          })
        );
        const nuevo: Record<string, number> = {};
        results.forEach((r) => {
          if (r.codigo) nuevo[r.codigo] = r.disponible;
        });
        setStockPorUbicacion(nuevo);
      } catch (error) {
        console.error('Error al cargar stock por ubicación:', error);
        setStockPorUbicacion({});
      }
    };

    fetchStock();
  }, [productos, document.ubicacion]);

  const handleStockInfoClick = (e: React.MouseEvent<HTMLElement>, codigoArticulo: string, descripcion: string, stockTotal?: number) => {
    e.stopPropagation();
    setStockInfoAnchorEl(e.currentTarget);
    setStockInfoProducto({ codigoArticulo, descripcion, stockTotal });
    setStockInfoData([]);
  };

  const handleStockInfoClose = () => {
    setStockInfoAnchorEl(null);
    setStockInfoProducto(null);
    setStockInfoData([]);
  };

  useEffect(() => {
    if (!stockInfoProducto?.codigoArticulo) return;
    const fetchStockInfo = async () => {
      setStockInfoLoading(true);
      try {
        const res = await inventarioInformes.getStockPorDeposito({
          codigoArticulo: stockInfoProducto.codigoArticulo,
          page: 1,
          pageSize: 50,
        });
        const data = (res.data || []).filter(
          (r: any) => (r.codigoArticulo || '').trim() === stockInfoProducto.codigoArticulo.trim()
        );
        setStockInfoData(data);
      } catch (error) {
        console.error('Error al cargar stock por ubicación:', error);
        setStockInfoData([]);
      } finally {
        setStockInfoLoading(false);
      }
    };
    fetchStockInfo();
  }, [stockInfoProducto?.codigoArticulo]);

  // Las cotizaciones también se cargarán bajo demanda cuando se necesiten

  // Cargar productos completos de los detalles cuando cambien, para tener acceso a los datos de IVA
  useEffect(() => {
    const cargarProductosDetalles = async () => {
      const codigosFaltantes = document.detalles
        .map(d => d.codigoArticulo)
        .filter((codigo): codigo is string => !!codigo && !productosDataCache[codigo]);
      
      if (codigosFaltantes.length > 0) {
        const productosCargados: Record<string, any> = {};
        await Promise.all(
          codigosFaltantes.map(async (codigo) => {
            try {
              const producto = await productoService.getProducto(codigo);
              productosCargados[codigo] = producto;
            } catch (error) {
              console.error(`Error al cargar producto ${codigo}:`, error);
            }
          })
        );
        if (Object.keys(productosCargados).length > 0) {
          setProductosDataCache(prev => ({ ...prev, ...productosCargados }));
        }
      }
    };
    
    if (document.detalles.length > 0) {
      cargarProductosDetalles();
    }
  }, [document.detalles.map(d => d.codigoArticulo).join(',')]);

  const applyDetalle = async (
    producto: any,
    cantidad: number,
    descuentoPct: number,
    precioListaEditado?: number | null,
    notasLinea?: string,
    masterLink?: { nroFormularioMaster: number; nroItemMaster: number },
  ) => {
    if (!clienteSeleccionado) {
      return;
    }
    const codigo = producto.codigoArticulo || producto.codigo;
    
    // Guardar el producto en el cache para tener acceso a los datos de IVA
    if (codigo && !productosDataCache[codigo]) {
      setProductosDataCache(prev => ({ ...prev, [codigo]: producto }));
    }
    
    const descripcion = producto.descripcion || producto.descripcionArticulo || '';
    const detalles = [...document.detalles];
    const idx = masterLink
      ? detalles.findIndex(
          (d) =>
            d.nroFormularioMaster === masterLink.nroFormularioMaster &&
            d.nroItemMaster === masterLink.nroItemMaster,
        )
      : detalles.findIndex((d) => d.codigoArticulo === codigo);

    // Si hay un precio de lista editado manualmente, usarlo directamente (ya está en ARS)
    // Si no, usar el precio base del producto y aplicar conversión si es necesario
    let unitBase: number;
    let usarPrecioEditado = false;
    let productoConMoneda = producto;
    
    if (precioListaEditado !== null && precioListaEditado !== undefined && precioListaEditado > 0) {
      // Si hay precio editado, asumimos que ya está en ARS (el usuario lo ingresa en ARS)
      unitBase = precioListaEditado;
      usarPrecioEditado = true;
    } else if (idx >= 0 && Number(detalles[idx].precioUnitario || 0) > 0) {
      // Si el detalle ya existe, reutilizar el precio guardado para evitar recálculos inesperados.
      unitBase = Number(detalles[idx].precioUnitario || 0);
      usarPrecioEditado = true;
    } else {
      unitBase = obtenerPrecioBaseProducto(producto);
      const nroLista = String(document.nroLista || '').trim();
      const codigoArticulo = String(codigo || '').trim();
      if (nroLista && codigoArticulo) {
        const registroLista = await obtenerArticuloLista(nroLista, codigoArticulo);
        const precioListaResuelto = resolverPrecioDesdeLista(producto, registroLista);
        if (precioListaResuelto.tipo === 'sinPrecio') {
          enqueueSnackbar(
            `El artículo ${codigoArticulo} no tiene precio configurado en la lista ${nroLista}. Se usará el precio base.`,
            { variant: 'warning' },
          );
        } else {
          unitBase = precioListaResuelto.precioLista;
          productoConMoneda = withCodigoMoneda(producto, precioListaResuelto.codigoMoneda);
        }
      }
    }
    
    let unit = unitBase;
    
    // Obtener codigoMoneda del producto y la cotización correspondiente
    const codigoMoneda = getCodigoMonedaProducto(productoConMoneda);
    
    // Si el producto tiene un codigoMoneda diferente de ARS y NO estamos usando un precio editado
    // (porque el precio editado ya está en ARS), obtener la cotización
    if (!usarPrecioEditado && codigoMoneda && codigoMoneda !== 0) {
      // Intentar obtener desde caché primero
      let cotizacion = getCotizacionPorProducto(productoConMoneda);
      
      // Si no está en caché, obtenerla
      if (!cotizacion) {
        cotizacion = await obtenerCotizacionPorProducto(productoConMoneda);
      }
      
      // Si hay cotización válida, convertir el precio
      // No redondear intermedio para mantener precisión: precioBase * cotizacion, luego redondear al final
      if (cotizacion && cotizacion > 0) {
        unit = round2(unitBase * cotizacion);
      }
      // Si no hay cotización, mantener el precio original en la moneda del producto
    }
    // Obtener unidad de medida del producto
    const um = producto.idUnidadMedida || producto.unidadMedida?.idUnidadMedida || producto.um || '';
    if (!cantidad || cantidad <= 0) {
      if (idx >= 0) {
        detalles.splice(idx, 1);
      }
    } else {
      const subtotal = cantidad * unit * (1 - (Number(descuentoPct) || 0) / 100);
      const descuentoValue = Number(descuentoPct) || 0;
      if (idx >= 0) {
        detalles[idx] = {
          ...detalles[idx],
          cantidad,
          precioUnitario: unit,
          descuento: descuentoValue,
          porcDesc: descuentoValue, // También guardar en porcDesc para compatibilidad
          subtotal,
          descripcionArticulo: descripcion,
          um: um || detalles[idx].um || '',
          codigoMoneda: 0, // Se establecerá desde el pedido
          cotizacion: 0, // Se establecerá desde el pedido
          ...(masterLink
            ? {
                nroFormularioMaster: masterLink.nroFormularioMaster,
                nroItemMaster: masterLink.nroItemMaster,
              }
            : {}),
          ...(notasLinea !== undefined
            ? { notas: normalizeItemNotas(notasLinea, VENTA_ITEM_NOTAS_MAX) }
            : {}),
        };
      } else {
        const nextNroItem =
          masterLink && detalles.length > 0
            ? Math.max(...detalles.map((d) => Number(d.nroItem || 0)), 0) + 1
            : detalles.length + 1;
        detalles.push({
          nroIdformulario: document.nroFormulario,
          nroItem: nextNroItem,
          codigoArticulo: codigo,
          descripcionArticulo: descripcion,
          cantidad,
          precioUnitario: unit,
          descuento: descuentoValue,
          porcDesc: descuentoValue, // También guardar en porcDesc para compatibilidad
          subtotal,
          um: um || '',
          codigoMoneda: 0, // Se establecerá desde el pedido
          cotizacion: 0, // Se establecerá desde el pedido
          ...(masterLink
            ? {
                nroFormularioMaster: masterLink.nroFormularioMaster,
                nroItemMaster: masterLink.nroItemMaster,
              }
            : {}),
          notas:
            notasLinea !== undefined
              ? normalizeItemNotas(notasLinea, VENTA_ITEM_NOTAS_MAX)
              : '',
        });
      }
    }
    const importeTotal = detalles.reduce((sum, det) => sum + (det.subtotal || 0), 0);
    onChange({ ...document, detalles, importeTotal });
  };

  const openBuscarPendientesMaster = async () => {
    if (!document.nroCliente || document.nroCliente <= 0) {
      return;
    }
    setOpenPendientesMaster(true);
    setLoadingPendientesMaster(true);
    setPendientesMasterRows([]);
    setCantidadesPendientesMaster({});
    try {
      const data = await ventasSeguimientoService.getPendientesMaster(document.nroCliente, true);
      const rows = (data || []).filter((p) =>
        hideFinancials ? Number(p.pendienteEntregar) > 0 : Number(p.pendienteFacturar) > 0
      );
      setPendientesMasterRows(rows);
      setCantidadesPendientesMaster(
        rows.reduce<Record<string, string>>((acc, row) => {
          acc[getPendienteMasterRowKey(row)] = String(getPendienteMasterCantidadSugerida(row));
          return acc;
        }, {}),
      );
    } catch {
      setPendientesMasterRows([]);
      setCantidadesPendientesMaster({});
      enqueueSnackbar('No se pudieron cargar los pendientes de entrega', { variant: 'error' });
    } finally {
      setLoadingPendientesMaster(false);
    }
  };

  const agregarPendienteDesdeMaster = async (
    p: VentaPendienteMaster,
    cantidadOverride?: number,
    silent = false,
  ): Promise<boolean> => {
    const yaAgregado = document.detalles.some(
      (d) => d.nroFormularioMaster === p.nroFormularioMaster && d.nroItemMaster === p.nroItemMaster,
    );
    if (yaAgregado) {
      if (!silent) {
        enqueueSnackbar('Ese pendiente ya fue agregado', { variant: 'info' });
      }
      return false;
    }
    const codigo = String(p.codigoArticulo || '').trim();
    if (!codigo) {
      if (!silent) {
        enqueueSnackbar('El artículo no tiene código', { variant: 'warning' });
      }
      return false;
    }
    let productoCompleto: any = {
      codigoArticulo: codigo,
      codigo,
      descripcion: p.descripcionArticulo,
      descripcionArticulo: p.descripcionArticulo,
    };
    try {
      productoCompleto = await productoService.getProducto(codigo);
    } catch {
      // se usa stub mínimo
    }
    const precioLista = Number(p.precioListaMaster ?? p.precioUnitarioMaster ?? 0) || 0;
    const porcDesc = Number(p.porcDesc1Master ?? 0) || 0;
    const cantidadPendienteEntregar = Number(p.pendienteEntregar) || 0;
    const cantidadPendienteFacturar = Number(p.pendienteFacturar) || 0;
    const cantidadDefault = hideFinancials
      ? (cantidadPendienteEntregar > 0
        ? cantidadPendienteEntregar
        : cantidadSugeridaDesdePendienteVenta(p))
      : (cantidadPendienteFacturar > 0
        ? cantidadPendienteFacturar
        : cantidadSugeridaDesdePendienteVenta(p));
    const cantidad = cantidadOverride ?? cantidadDefault;
    if (!(cantidad > 0)) {
      if (!silent) {
        enqueueSnackbar('La cantidad debe ser mayor a cero', { variant: 'warning' });
      }
      return false;
    }

    await applyDetalle(
      productoCompleto,
      cantidad,
      porcDesc,
      precioLista > 0 ? precioLista : null,
      undefined,
      { nroFormularioMaster: p.nroFormularioMaster, nroItemMaster: p.nroItemMaster },
    );
    if (!silent) {
      enqueueSnackbar('Línea agregada desde pendientes', { variant: 'success' });
    }
    return true;
  };

  const agregarTodosPendientesDesdeMaster = async () => {
    if (!pendientesMasterRows.length) {
      return;
    }

    let agregadas = 0;
    let omitidas = 0;
    for (const pendiente of pendientesMasterRows) {
      const cantidad = parsePendienteMasterCantidadEditable(pendiente);
      if (!(cantidad > 0)) {
        omitidas += 1;
        continue;
      }
      const ok = await agregarPendienteDesdeMaster(pendiente, cantidad, true);
      if (ok) {
        agregadas += 1;
      } else {
        omitidas += 1;
      }
    }

    if (agregadas > 0) {
      enqueueSnackbar(
        omitidas > 0
          ? `Se agregaron ${agregadas} líneas. ${omitidas} quedaron omitidas.`
          : `Se agregaron ${agregadas} líneas desde pendientes.`,
        { variant: 'success' },
      );
      return;
    }
    enqueueSnackbar('No se pudo agregar ninguna línea desde pendientes', { variant: 'info' });
  };

  useEffect(() => {
    if (!pendienteHiloInfo) {
      setPendienteHiloItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPendienteHilo(true);
      try {
        const data = await ventasSeguimientoService.getHilo(
          pendienteHiloInfo.nroFormularioMaster,
          pendienteHiloInfo.nroItemMaster,
        );
        if (!cancelled) {
          setPendienteHiloItems(data.comprobantes || []);
        }
      } catch {
        if (!cancelled) {
          setPendienteHiloItems([]);
          enqueueSnackbar('No se pudo cargar el detalle del hilo', { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingPendienteHilo(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendienteHiloInfo, enqueueSnackbar]);

  const updateDetalleCantidad = (producto: any, delta: number) => {
    if (!clienteSeleccionado) {
      return;
    }
    const codigo = producto.codigoArticulo || producto.codigo;

    const detalles = [...document.detalles];
    const idx = detalles.findIndex(d => d.codigoArticulo === codigo);
    if (idx >= 0) {
      const nuevaCantidad = Math.max(0, (detalles[idx].cantidad || 0) + delta);
      const descuento = Number(detalles[idx].descuento ?? detalles[idx].porcDesc ?? 0);
      applyDetalle(producto, nuevaCantidad, descuento);
      return;
    } else if (delta > 0) {
      // Si es un producto nuevo, usar el descuento del producto si existe
      const descuentoProducto = producto.porcDesc ?? producto.porc_Desc ?? producto.descuento ?? 0;
      applyDetalle(producto, delta, Number(descuentoProducto));
      return;
    }
    const importeTotal = document.detalles.reduce((sum, det) => sum + (det.subtotal || 0), 0);
    onChange({ ...document, importeTotal });
  };
  const openProductDialog = async (p: any) => {
    const codigo = p.codigoArticulo || p.codigo;
    
    // Cargar el producto completo si no está en el cache
    let productoCompleto = p;
    if (!productosDataCache[codigo]) {
      try {
        productoCompleto = await productoService.getProducto(codigo);
        setProductosDataCache(prev => ({ ...prev, [codigo]: productoCompleto }));
      } catch (error) {
        console.error('Error al cargar el producto completo:', error);
        // Si falla, usar el producto que ya tenemos
        productoCompleto = p;
        setProductosDataCache(prev => ({ ...prev, [codigo]: p }));
      }
    } else {
      productoCompleto = productosDataCache[codigo];
    }
    
    setSelectedProduct(productoCompleto);
    const det = document.detalles.find(d => d.codigoArticulo === codigo);
    setDialogQty(det?.cantidad || 1);
    setDialogNotas(normalizeItemNotas(det?.notas, VENTA_ITEM_NOTAS_MAX));
    // Si hay un detalle existente, usar su descuento; si no, usar el descuento del producto si existe
    const descuentoDetalle = det?.descuento ?? det?.porcDesc;
    const descuentoProducto = productoCompleto.porcDesc ?? productoCompleto.porc_Desc ?? productoCompleto.descuento;
    setDialogDiscount(Number(descuentoDetalle ?? descuentoProducto ?? 0));
    
    // Inicializar el precio de lista editable con el precio actual del detalle o del producto
    // Si hay un detalle existente, usar su precio (ya está en ARS); si no, calcular el precio base
    const precioDetalle = det?.precioUnitario;
    if (precioDetalle && precioDetalle > 0) {
      // Si hay un detalle existente, usar su precio (ya está convertido a ARS si era necesario)
      setDialogPrecioLista(precioDetalle);
    } else {
      // Si no hay detalle, calcular el precio base considerando la moneda del producto
      let precioListaBase = obtenerPrecioBaseProducto(productoCompleto);
      let productoConMoneda = productoCompleto;
      const nroLista = String(document.nroLista || '').trim();
      if (nroLista && codigo) {
        const registroLista = await obtenerArticuloLista(nroLista, codigo);
        const precioListaResuelto = resolverPrecioDesdeLista(productoCompleto, registroLista);
        if (precioListaResuelto.tipo !== 'sinPrecio') {
          precioListaBase = precioListaResuelto.precioLista;
          productoConMoneda = withCodigoMoneda(productoCompleto, precioListaResuelto.codigoMoneda);
        }
      }
      const codigoMoneda = getCodigoMonedaProducto(productoConMoneda);
      if (codigoMoneda && codigoMoneda !== 0) {
        // Si el producto tiene moneda especial, obtener la cotización y convertir
        let cotizacion = getCotizacionPorProducto(productoConMoneda);
        if (!cotizacion && !cotizacionesCargando[codigoMoneda]) {
          cotizacion = await obtenerCotizacionPorProducto(productoConMoneda);
        }
        if (cotizacion && cotizacion > 0) {
          setDialogPrecioLista(round2(precioListaBase * cotizacion));
        } else {
          setDialogPrecioLista(precioListaBase);
        }
      } else {
        setDialogPrecioLista(precioListaBase);
      }
    }
    
    // Precargar la cotización si el producto tiene un codigoMoneda diferente de ARS
    const codigoMoneda = getCodigoMonedaProducto(productoCompleto);
    if (codigoMoneda && codigoMoneda !== 0 && !cotizacionesCache.hasOwnProperty(codigoMoneda) && !cotizacionesCargando[codigoMoneda]) {
      obtenerCotizacionPorProducto(productoCompleto);
    }
    
    setProductDialogOpen(true);
  };

  const openProductDialogFromDetalle = async (detalle: PedidoVentaDetalle) => {
    const codigoArticulo = detalle.codigoArticulo;
    if (!codigoArticulo) {
      return;
    }
    try {
      // Buscar el producto completo por código
      let producto = productosDataCache[codigoArticulo];
      if (!producto) {
        producto = await productoService.getProducto(codigoArticulo);
        setProductosDataCache(prev => ({ ...prev, [codigoArticulo]: producto }));
      }
      if (producto) {
        setSelectedProduct(producto);
        setDialogQty(detalle.cantidad || 1);
        setDialogDiscount(Number(detalle.descuento || 0));
        // Inicializar el precio de lista editable con el precio del detalle
        setDialogPrecioLista(detalle.precioUnitario || 0);
        setDialogNotas(normalizeItemNotas(detalle.notas, VENTA_ITEM_NOTAS_MAX));
        setProductDialogOpen(true);
      }
    } catch (error) {
      console.error('Error al cargar el producto:', error);
      // Si no se puede cargar el producto completo, usar la información del detalle
      const productoFromDetalle = {
        codigoArticulo: codigoArticulo,
        codigo: codigoArticulo,
        descripcion: detalle.descripcionArticulo,
        descripcionArticulo: detalle.descripcionArticulo,
        precioVenta: detalle.precioUnitario,
        precioLista: detalle.precioUnitario,
        precioUnitario: detalle.precioUnitario,
        precio: detalle.precioUnitario,
      };
      setProductosDataCache(prev => ({ ...prev, [codigoArticulo]: productoFromDetalle }));
      setSelectedProduct(productoFromDetalle);
      setDialogQty(detalle.cantidad || 1);
      setDialogDiscount(Number(detalle.descuento || 0));
      // Inicializar el precio de lista editable con el precio del detalle
      setDialogPrecioLista(detalle.precioUnitario || 0);
      setDialogNotas(normalizeItemNotas(detalle.notas, VENTA_ITEM_NOTAS_MAX));
      setProductDialogOpen(true);
    }
  };
  const closeProductDialog = () => {
    setProductDialogOpen(false);
    setSelectedProduct(null);
    setDialogPrecioLista(null);
    setDialogNotas('');
  };
  const handleActualizarProducto = () => {
    if (!clienteSeleccionado) {
      return;
    }
    if (selectedProduct) {
      // Obtener el codigoRamo del producto para verificar si se puede editar el precio
      const codigoRamo = selectedProduct.codigoRamo;
      const puedeEditarPrecio = codigoRamo ? ramoEditaPrecioCache[codigoRamo] : false;
      
      // Si se puede editar el precio y hay un precio editado, usarlo; si no, pasar null
      const precioListaEditado = puedeEditarPrecio && dialogPrecioLista !== null && dialogPrecioLista > 0
        ? dialogPrecioLista
        : null;
      
      applyDetalle(
        selectedProduct,
        Math.max(0, dialogQty || 0),
        Math.max(0, Math.min(100, dialogDiscount || 0)),
        precioListaEditado,
        dialogNotas,
      );
    }
    closeProductDialog();
  };

  const handleEliminarProducto = () => {
    if (selectedProduct) {
      // Establecer cantidad en 0 para eliminar el item
      applyDetalle(selectedProduct, 0, 0);
    }
    closeProductDialog();
  };

  // Cargar nombre de categoría (Ramo) al abrir diálogo si no está en cache
  useEffect(() => {
    const loadRamo = async () => {
      const codigoRamo = selectedProduct?.codigoRamo;
      if (!codigoRamo) return;
      
      // Cargar nombre del ramo si no está en cache
      if (!ramoCache[codigoRamo]) {
        try {
          const r = await catalogosService.getRamo(String(codigoRamo));
          setRamoCache(prev => ({ ...prev, [String(codigoRamo)]: r?.nombreRamo || String(codigoRamo) }));
        } catch {
          setRamoCache(prev => ({ ...prev, [String(codigoRamo)]: String(codigoRamo) }));
        }
      }
      
      // Cargar editaPrecio del ramo si no está en cache
      if (!ramoEditaPrecioCache.hasOwnProperty(codigoRamo)) {
        try {
          const r = await catalogosService.getRamo(String(codigoRamo));
          const editaPrecio = r?.editaPrecio === 1;
          setRamoEditaPrecioCache(prev => ({ ...prev, [String(codigoRamo)]: editaPrecio }));
        } catch {
          setRamoEditaPrecioCache(prev => ({ ...prev, [String(codigoRamo)]: false }));
        }
      }
    };
    loadRamo();
  }, [selectedProduct, ramoCache, ramoEditaPrecioCache]);

  useEffect(() => {
    const n = Number(document.nroVendedor);
    if (!Number.isFinite(n) || n <= 0) {
      setVendedorSeleccionado(null);
      return;
    }
    let cancelled = false;
    vendedorService
      .getVendedor(n)
      .then((v) => {
        if (!cancelled && v) {
          setVendedorSeleccionado({ nroVendedor: v.nroVendedor, nombreVendedor: v.nombreVendedor });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVendedorSeleccionado({ nroVendedor: n, nombreVendedor: '' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [document.nroVendedor]);

  useEffect(() => {
    const codigoLista = String(document.nroLista || '').trim();
    if (!codigoLista) {
      setListaPrecioSeleccionada(null);
      return;
    }

    let cancelled = false;
    listaPrecioService
      .getListaPrecio(codigoLista)
      .then((lista) => {
        if (!cancelled && lista) {
          setListaPrecioSeleccionada({
            codigoLista: String(lista.codigoLista || codigoLista).trim(),
            descripcion: String(lista.descripcion || '').trim(),
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setListaPrecioSeleccionada({
            codigoLista,
            descripcion: '',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [document.nroLista]);

  const cantidadItems = document.detalles.length;
  const cantidadTotal = document.detalles.reduce((sum, d) => sum + (d.cantidad || 0), 0);
  const INFO_TAB_INDEX = 0;
  const CATALOGO_TAB_INDEX = 1;
  const RESUMEN_TAB_INDEX = readOnly ? 1 : 2;
  const hasCatalogTab = !readOnly;

  useEffect(() => {
    setCurrentTab((prevTab) => {
      const maxTabIndex = RESUMEN_TAB_INDEX;
      return prevTab > maxTabIndex ? maxTabIndex : prevTab;
    });
  }, [RESUMEN_TAB_INDEX]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleSucursalChange = (event: SelectChangeEvent<number | string>) => {
    const value = Number(event.target.value);
    onChange({
      ...document,
      nroSuc: Number.isFinite(value) ? value : 0,
    } as FormularioVentaFormDocument);
  };

  const handleSelectVendedor = (v: Vendedor) => {
    setVendedorSeleccionado({ nroVendedor: v.nroVendedor, nombreVendedor: v.nombreVendedor });
    onChange({ ...document, nroVendedor: v.nroVendedor } as FormularioVentaFormDocument);
    setOpenVendedorSearch(false);
  };

  const handleSelectListaPrecio = (listaPrecio: ListaPrecio) => {
    const codigoLista = String(listaPrecio.codigoLista || '').trim();
    const descripcion = String(listaPrecio.descripcion || '').trim();
    setListaPrecioSeleccionada({
      codigoLista,
      descripcion,
    });
    onChange({
      ...document,
      nroLista: codigoLista,
    } as FormularioVentaFormDocument);
    setOpenListaPrecioSearch(false);
  };

  return (
    <>
      {/* Selección de cliente - igual a Nuevo Recibo */}
      <Card sx={{ p: 2, mb: 2, boxShadow: 2, borderRadius: 2 }}>
        <Grid container spacing={3}>         
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Compañía</InputLabel>
              <Select
                label="Compañía"
                value={document.idCompania || ''}
                onChange={(e: SelectChangeEvent<string>) =>
                  onChange({
                    ...document,
                    idCompania: e.target.value || '',
                  } as FormularioVentaFormDocument)
                }
                disabled={readOnly}
              >
                <MenuItem value="">
                  <em>Seleccione compañía</em>
                </MenuItem>
                {companias.map((c) => (
                  <MenuItem key={c.idCompania} value={c.idCompania}>
                    {c.idCompania} - {c.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required disabled={loadingSucursales || readOnly}>
              <InputLabel>Nº Sucursal</InputLabel>
              <Select
                name="nroSuc"
                label="Nº Sucursal"
                value={document.nroSuc && document.nroSuc > 0 ? document.nroSuc : ''}
                onChange={handleSucursalChange}
                disabled={readOnly}
              >
                {loadingSucursales && (
                  <MenuItem disabled>
                    Cargando sucursales...
                  </MenuItem>
                )}
                {!loadingSucursales && sucursalesOrdenadas.map((sucursal) => (
                  <MenuItem key={sucursal.nroSuc} value={sucursal.nroSuc}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1,
                      }}
                    >
                      <span>
                        {sucursal.nroSuc} - {sucursal.nombreSucursal?.trim() || `Sucursal ${sucursal.nroSuc}`}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Número de Cliente"
              name="nroCliente"
              value={document.nroCliente}
              onChange={(e) => onChange({
                ...document,
                nroCliente: Number(e.target.value) || 0,
              } as FormularioVentaFormDocument)}
              helperText="Escriba el número para buscar automáticamente"
              disabled={readOnly}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={onClienteSearch} edge="end" color="primary" title="Seleccionar cliente" disabled={readOnly}>
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombre"
              name="nombre"
              value={document.nombre}
              onChange={(e) => onChange({
                ...document,
                nombre: e.target.value,
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {esConsumidorFinal ? (
              <TextField
                fullWidth
                label="DNI"
                name="dni"
                value={document.dni || ''}
                onChange={(e) =>
                  onChange({
                    ...document,
                    dni: e.target.value,
                  } as FormularioVentaFormDocument)
                }
              />
            ) : (
              <TextField
                fullWidth
                label="CUIT"
                name="cuit"
                value={document.cuit || ''}
                onChange={(e) =>
                  onChange({
                    ...document,
                    cuit: e.target.value,
                  } as FormularioVentaFormDocument)
                }
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Tipo IVA</InputLabel>
              <Select
                name="tipoIVA"
                value={document.tipoIVA || ''}
                label="Tipo IVA"
                required
                onChange={(e) => onChange({
                  ...document,
                  tipoIVA: e.target.value,
                } as FormularioVentaFormDocument)}
                sx={{
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  },
                }}
              >
                <MenuItem value="">Seleccione...</MenuItem>
                <MenuItem value="I">Responsable Inscripto</MenuItem>
                <MenuItem value="C">Consumidor Final</MenuItem>
                <MenuItem value="M">Monotributo</MenuItem>
                <MenuItem value="E">Exento</MenuItem>
                <MenuItem value="N">No Categorizado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Tabs para las secciones */}
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
        <Box sx={{ position: 'relative', borderBottom: 1, borderColor: 'divider', minHeight: 56, px: 7 }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label={ariaLabelTabs}
          >
            <Tab label={tabLabelInfo} />
            {hasCatalogTab && <Tab label="Catálogo de Productos" />}
            <Tab label={tabLabelResumen} />
          </Tabs>
          {/* Flechas de navegación en las esquinas de los tabs */}
          {currentTab === INFO_TAB_INDEX && (
            <IconButton
              aria-label="siguiente"
              onClick={() => setCurrentTab(hasCatalogTab ? CATALOGO_TAB_INDEX : RESUMEN_TAB_INDEX)}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'primary.light',
                color: 'white',
                borderRadius: '50%',
                boxShadow: 3,
                '&:hover': { bgcolor: 'primary.main' },
                zIndex: 2
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          )}
          {hasCatalogTab && currentTab === CATALOGO_TAB_INDEX && (
            <>
              <IconButton
                aria-label="anterior"
                onClick={() => setCurrentTab(INFO_TAB_INDEX)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  bgcolor: 'primary.light',
                  color: 'white',
                  borderRadius: '50%',
                  boxShadow: 3,
                  '&:hover': { bgcolor: 'primary.main' },
                  zIndex: 2
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label="siguiente"
                onClick={() => setCurrentTab(RESUMEN_TAB_INDEX)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'primary.light',
                  color: 'white',
                  borderRadius: '50%',
                  boxShadow: 3,
                  '&:hover': { bgcolor: 'primary.main' },
                  zIndex: 2
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </>
          )}
          {currentTab === RESUMEN_TAB_INDEX && (
            <IconButton
              aria-label="anterior"
              onClick={() => setCurrentTab(hasCatalogTab ? CATALOGO_TAB_INDEX : INFO_TAB_INDEX)}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                bgcolor: 'primary.light',
                color: 'white',
                borderRadius: '50%',
                boxShadow: 3,
                '&:hover': { bgcolor: 'primary.main' },
                zIndex: 2
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Información del pedido */}
        {currentTab === INFO_TAB_INDEX && (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Fecha de Emisión"
              value={
                document.fechaEmision
                  ? (document.fechaEmision instanceof Date
                      ? document.fechaEmision.toISOString().split('T')[0]
                      : new Date(document.fechaEmision).toISOString().split('T')[0])
                  : ''
              }
              onChange={(e) => onChange({
                ...document,
                fechaEmision: parseLocalDate(e.target.value),
              })}
              disabled={readOnly}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Orden de Compra"
              value={document.ordenCompra || ''}
              onChange={(e) => onChange({
                ...document,
                ordenCompra: e.target.value,
              } as FormularioVentaFormDocument)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Transporte</InputLabel>
              <Select
                label="Transporte"
                value={document.transporte || ''}
                onChange={(e) => onChange({
                  ...document,
                  transporte: String(e.target.value || ''),
                } as FormularioVentaFormDocument)}
              >
                <MenuItem value=""><em>Seleccione transporte</em></MenuItem>
                {transportes.map((t) => {
                  // Mostrar el nombreExpreso tal cual como viene del GET api/Transportes
                  // Con todos los espacios, sin modificaciones
                  const nombreMostrar = (t.nombreExpreso ?? t.nombre ?? '').trim();
                  const nroMostrar = String(t.nroExpreso ?? t.codigo ?? t.id ?? '');
                  return (
                    <MenuItem
                      key={String(t.id ?? t.nroExpreso ?? t.codigo)}
                      value={String(t.nroExpreso ?? t.id ?? t.codigo)}
                    >
                      {nombreMostrar + ' (' + nroMostrar + ')'}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Ubicación"
              value={document.ubicacion || ''}
              onClick={() => setUbicacionDialogOpen(true)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setUbicacionDialogOpen(true)} edge="end" color="primary">
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Forma de Pago"
              value={document.formaDePago || ''}
              onClick={() => setFormaDePagoDialogOpen(true)}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setFormaDePagoDialogOpen(true)} edge="end" color="primary">
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <PaymentIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Lista de Precios"
              value={
                listaPrecioSeleccionada
                  ? `${listaPrecioSeleccionada.codigoLista.trim()} - ${listaPrecioSeleccionada.descripcion?.trim() || ''}`
                  : (document.nroLista || '')
              }
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenListaPrecioSearch(true)}
                      edge="end"
                      color="primary"
                      title="Buscar lista de precios"
                      disabled={readOnly}
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nº Vendedor"
              name="nroVendedor"
              value={
                vendedorSeleccionado
                  ? `${vendedorSeleccionado.nroVendedor} - ${vendedorSeleccionado.nombreVendedor?.trim() || ''}`
                  : document.nroVendedor
                    ? String(document.nroVendedor)
                    : ''
              }
              onChange={(e) => {
                const numPart = e.target.value.trim().split(/\s*-\s*/)[0].trim();
                const num = parseInt(numPart, 10);
                onChange({
                  ...document,
                  nroVendedor: Number.isFinite(num) && num > 0 ? num : 0,
                } as FormularioVentaFormDocument);
                if (!numPart || !Number.isFinite(num) || num <= 0) {
                  setVendedorSeleccionado(null);
                }
              }}
              disabled={readOnly}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenVendedorSearch(true)}
                      edge="end"
                      color="primary"
                      title="Buscar vendedor"
                      disabled={readOnly}
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Observaciones"
              value={document.descripcion}
              onChange={(e) => onChange({
                ...document,
                descripcion: e.target.value,
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <NotesIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mt={3}>
            <Box display="flex" gap={3} alignItems="center">
              <Typography variant="body2">Artículos ({cantidadItems})</Typography>
              <Typography variant="body2">Cantidad total: {cantidadTotal}</Typography>
            </Box>
            {!hideFinancials && (
              <Typography variant="h6">{totalLabel}: {formatCurrency(totalCalculado)}</Typography>
            )}
          </Box>
        </Box>
        )}

        {/* Catálogo de Productos */}
        {hasCatalogTab && currentTab === CATALOGO_TAB_INDEX && (
        <Box sx={{ p: 2 }}>
        {!clienteSeleccionado && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Debe seleccionar un cliente antes de agregar productos al carrito.
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            placeholder="Buscar productos..."
            value={prodSearch}
            onChange={(e) => setProdSearch(e.target.value)}
            sx={{ flex: '1 1 200px', minWidth: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          {hideFinancials && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => void openBuscarPendientesMaster()}
              disabled={readOnly || !clienteSeleccionado}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Buscar pendientes
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 0.5, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 0.5, flexShrink: 0 }}>
            <IconButton
              size="small"
              onClick={() => setVistaProductos('cuadrados')}
              sx={{
                color: vistaProductos === 'cuadrados' ? 'primary.main' : 'text.secondary',
                bgcolor: vistaProductos === 'cuadrados' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: vistaProductos === 'cuadrados' ? 'primary.light' : 'action.hover' }
              }}
              title="Vista cuadrados"
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setVistaProductos('tarjetas')}
              sx={{
                color: vistaProductos === 'tarjetas' ? 'primary.main' : 'text.secondary',
                bgcolor: vistaProductos === 'tarjetas' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: vistaProductos === 'tarjetas' ? 'primary.light' : 'action.hover' }
              }}
              title="Vista tarjetas horizontales"
            >
              <ViewAgendaIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setVistaProductos('renglones')}
              sx={{
                color: vistaProductos === 'renglones' ? 'primary.main' : 'text.secondary',
                bgcolor: vistaProductos === 'renglones' ? 'primary.light' : 'transparent',
                '&:hover': { bgcolor: vistaProductos === 'renglones' ? 'primary.light' : 'action.hover' }
              }}
              title="Vista renglones"
            >
              <ViewListIcon />
            </IconButton>
          </Box>
        </Box>

        {vistaProductos === 'cuadrados' && (
          <Grid container spacing={2}>
            {productos.map((p) => {
              const codigo = p.codigoArticulo || p.codigo;
              const descripcion = p.descripcion || p.descripcionArticulo || '';
              const detalle = document.detalles.find(d => d.codigoArticulo === codigo);
              const cantidad = detalle?.cantidad || 0;
              const stock = p.cantidad ?? p.stock ?? p.stockTotal ?? p.existencia;
              const imgSrc = getProductoImageBySize(p, 'thumbnail');
              const { precio, tieneCotizacion, codigoMoneda, productoParaMoneda } = resolverPrecioCatalogo(p);
              const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
              const noHayCotizacionParaEstaMoneda = Boolean(esMonedaEspecial && codigoMoneda !== null && codigoMoneda !== 0 && noHayCotizacionDisponible(codigoMoneda));
              return (
                <Grid item xs={12} sm={6} md={4} key={codigo}>
                  <Card
                    onClick={() => openProductDialog(p)}
                    sx={{ 
                      cursor: 'pointer', 
                      borderRadius: 2, 
                      boxShadow: 1,
                      border: esMonedaEspecial ? '1px solid' : 'none',
                      borderColor: esMonedaEspecial ? 'warning.main' : 'transparent',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: '100%',
                          height: 120,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                        }}
                      >
                        <CardMedia
                          component="img"
                          src={imgSrc}
                          alt={descripcion}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e: any) => {
                            e.currentTarget.src = getProductoFallbackImage();
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: 'primary.light' }}>{codigo}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{descripcion}</Typography>
                      {!hideFinancials && noHayCotizacionParaEstaMoneda ? (
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ color: 'error.main', fontWeight: 700 }}>N/A</Typography>
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProduct(p);
                              setMonedaInfoDialogOpen(true);
                            }}
                            sx={{
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                              width: 20,
                              height: 20,
                            }}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <text x="3" y="15" fontSize="10" fill="#424242" fontWeight="bold">$</text>
                              <text x="17" y="15" fontSize="10" fill="#424242" fontWeight="bold">¥</text>
                              <path d="M 7 5 Q 12 3 17 5" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                              <path d="M 17 19 Q 12 21 7 19" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                              <path d="M 15 4 L 17 5 L 15 6" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M 9 20 L 7 19 L 9 18" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </Box>
                        </Box>
                      ) : !hideFinancials ? (
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>{formatearPrecioPorTipoMoneda(precio, productoParaMoneda, tieneCotizacion)}</Typography>
                      ) : null}
                      {stock !== undefined && (
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip label={`Cant.Total: ${stock}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', color: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                          {document.ubicacion && stockPorUbicacion[codigo] !== undefined && (
                            <Chip label={`Cant.Deposito: ${stockPorUbicacion[codigo]}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', color: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                          )}
                          <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleStockInfoClick(e, codigo, descripcion, stock)} aria-label="Ver stock por ubicación">
                            <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />
                          </IconButton>
                        </Stack>
                      )}
                      <Box display="flex" alignItems="center" gap={1} mt={1} onClick={(e) => e.stopPropagation()}>
                        <IconButton 
                          size="small" 
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, -1); }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField 
                          size="small" 
                          type="number" 
                          value={cantidad} 
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          onClick={(e) => e.stopPropagation()} 
                          onChange={(e) => {
                            if (clienteSeleccionado && !noHayCotizacionParaEstaMoneda) {
                              const newQty = Math.max(0, Number(e.target.value) || 0);
                              const diff = newQty - cantidad;
                              if (diff !== 0) updateDetalleCantidad(p, diff);
                            }
                          }} 
                          sx={{ 
                            width: 70,
                            '& .MuiInputBase-input.Mui-disabled': {
                              color: 'text.disabled',
                              WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                            },
                            '& .MuiInputBase-root.Mui-disabled': {
                              bgcolor: 'grey.100',
                            }
                          }} 
                          inputProps={{ min: 0 }} 
                        />
                        <IconButton 
                          size="small" 
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, 1); }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {vistaProductos === 'tarjetas' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {productos.map((p) => {
              const codigo = p.codigoArticulo || p.codigo;
              const descripcion = p.descripcion || p.descripcionArticulo || '';
              const detalle = document.detalles.find(d => d.codigoArticulo === codigo);
              const cantidad = detalle?.cantidad || 0;
              const stock = p.cantidad ?? p.stock ?? p.stockTotal ?? p.existencia;
              const imgSrc = getProductoImageBySize(p, 'thumbnail');
              const { precio, tieneCotizacion, codigoMoneda, productoParaMoneda } = resolverPrecioCatalogo(p);
              const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
              const noHayCotizacionParaEstaMoneda = Boolean(esMonedaEspecial && codigoMoneda !== null && codigoMoneda !== 0 && noHayCotizacionDisponible(codigoMoneda));
              return (
                <Card
                  key={codigo}
                  onClick={() => openProductDialog(p)}
                  sx={{ 
                    cursor: 'pointer', 
                    borderRadius: 2, 
                    boxShadow: 1, 
                    '&:hover': { boxShadow: 3 },
                    border: esMonedaEspecial ? '1px solid' : 'none',
                    borderColor: esMonedaEspecial ? 'warning.main' : 'transparent',
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: 150,
                          height: 120,
                          minWidth: 150,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CardMedia
                          component="img"
                          src={imgSrc}
                          alt={descripcion}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e: any) => {
                            e.currentTarget.src = getProductoFallbackImage();
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'primary.light', display: 'block', mb: 0.5 }}>{codigo}</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>{descripcion}</Typography>
                          {!hideFinancials && noHayCotizacionParaEstaMoneda ? (
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                              <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>N/A</Typography>
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProduct(p);
                                  setMonedaInfoDialogOpen(true);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  width: 22,
                                  height: 22,
                                }}
                              >
                                <svg
                                  width="22"
                                  height="22"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <text x="3" y="15" fontSize="11" fill="#424242" fontWeight="bold">$</text>
                                  <text x="17" y="15" fontSize="11" fill="#424242" fontWeight="bold">¥</text>
                                  <path d="M 7 5 Q 12 3 17 5" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 17 19 Q 12 21 7 19" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 15 4 L 17 5 L 15 6" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M 9 20 L 7 19 L 9 18" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </Box>
                            </Box>
                          ) : !hideFinancials ? (
                            <Typography variant="h6" color="primary" sx={{ mb: 0.5, fontWeight: 700 }}>{formatearPrecioPorTipoMoneda(precio, productoParaMoneda, tieneCotizacion)}</Typography>
                          ) : null}
                          {stock !== undefined && (
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                              <Chip label={`Cant.Total: ${stock}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 24, bgcolor: 'white', borderColor: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', color: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                              {document.ubicacion && stockPorUbicacion[codigo] !== undefined && (
                                <Chip label={`Cant.Deposito: ${stockPorUbicacion[codigo]}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 24, bgcolor: 'white', borderColor: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', color: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                              )}
                              <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleStockInfoClick(e, codigo, descripcion, stock)} aria-label="Ver stock por ubicación">
                                <InfoIcon sx={{ fontSize: 18, color: 'info.main' }} />
                              </IconButton>
                            </Stack>
                          )}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
                          <IconButton 
                            size="small" 
                            disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                            onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, -1); }}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <TextField 
                            size="small" 
                            type="number" 
                            value={cantidad} 
                            disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                            onClick={(e) => e.stopPropagation()} 
                            onChange={(e) => {
                              if (clienteSeleccionado && !noHayCotizacionParaEstaMoneda) {
                                const newQty = Math.max(0, Number(e.target.value) || 0);
                                const diff = newQty - cantidad;
                                if (diff !== 0) updateDetalleCantidad(p, diff);
                              }
                            }} 
                            sx={{ 
                              width: 70,
                              '& .MuiInputBase-input.Mui-disabled': {
                                color: 'text.disabled',
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                              },
                              '& .MuiInputBase-root.Mui-disabled': {
                                bgcolor: 'grey.100',
                              }
                            }} 
                            inputProps={{ min: 0 }} 
                          />
                          <IconButton 
                            size="small" 
                            disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                            onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, 1); }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {vistaProductos === 'renglones' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {productos.map((p) => {
              const codigo = p.codigoArticulo || p.codigo;
              const descripcion = p.descripcion || p.descripcionArticulo || '';
              const detalle = document.detalles.find(d => d.codigoArticulo === codigo);
              const cantidad = detalle?.cantidad || 0;
              const stock = p.cantidad ?? p.stock ?? p.stockTotal ?? p.existencia;
              const imgSrc = getProductoImageBySize(p, 'thumbnail');
              const { precio, tieneCotizacion, codigoMoneda, productoParaMoneda } = resolverPrecioCatalogo(p);
              const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
              const noHayCotizacionParaEstaMoneda = Boolean(esMonedaEspecial && codigoMoneda !== null && codigoMoneda !== 0 && noHayCotizacionDisponible(codigoMoneda));
              return (
                <Card
                  key={codigo}
                  onClick={() => openProductDialog(p)}
                  sx={{ 
                    cursor: 'pointer', 
                    borderRadius: 1, 
                    boxShadow: 1, 
                    '&:hover': { boxShadow: 2 },
                    border: esMonedaEspecial ? '1px solid' : 'none',
                    borderColor: esMonedaEspecial ? 'warning.main' : 'transparent',
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          minWidth: 80,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          bgcolor: 'grey.50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CardMedia
                          component="img"
                          src={imgSrc}
                          alt={descripcion}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e: any) => {
                            e.currentTarget.src = getProductoFallbackImage();
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: 'primary.light', display: 'block' }}>{codigo}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{descripcion}</Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }}>
                          {!hideFinancials && noHayCotizacionParaEstaMoneda ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>N/A</Typography>
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProduct(p);
                                  setMonedaInfoDialogOpen(true);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  width: 18,
                                  height: 18,
                                }}
                              >
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <text x="3" y="15" fontSize="9" fill="#424242" fontWeight="bold">$</text>
                                  <text x="17" y="15" fontSize="9" fill="#424242" fontWeight="bold">¥</text>
                                  <path d="M 7 5 Q 12 3 17 5" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 17 19 Q 12 21 7 19" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 15 4 L 17 5 L 15 6" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M 9 20 L 7 19 L 9 18" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </Box>
                            </Box>
                          ) : !hideFinancials ? (
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>{formatearPrecioPorTipoMoneda(precio, productoParaMoneda, tieneCotizacion)}</Typography>
                          ) : null}
                          {stock !== undefined && (
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Chip label={`Cant.Total: ${stock}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', color: (typeof stock === 'number' && stock > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                              {document.ubicacion && stockPorUbicacion[codigo] !== undefined && (
                                <Chip label={`Cant.Deposito: ${stockPorUbicacion[codigo]}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codigo, descripcion, stock); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', color: (typeof stockPorUbicacion[codigo] === 'number' && stockPorUbicacion[codigo] > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                              )}
                              <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleStockInfoClick(e, codigo, descripcion, stock)} aria-label="Ver stock por ubicación">
                                <InfoIcon sx={{ fontSize: 14, color: 'info.main' }} />
                              </IconButton>
                            </Stack>
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5} onClick={(e) => e.stopPropagation()}>
                        <IconButton 
                          size="small" 
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, -1); }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField 
                          size="small" 
                          type="number" 
                          value={cantidad} 
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          onClick={(e) => e.stopPropagation()} 
                          onChange={(e) => {
                            if (clienteSeleccionado && !noHayCotizacionParaEstaMoneda) {
                              const newQty = Math.max(0, Number(e.target.value) || 0);
                              const diff = newQty - cantidad;
                              if (diff !== 0) updateDetalleCantidad(p, diff);
                            }
                          }}
                          sx={{ 
                            width: 60,
                            '& .MuiInputBase-input.Mui-disabled': {
                              color: 'text.disabled',
                              WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                            },
                            '& .MuiInputBase-root.Mui-disabled': {
                              bgcolor: 'grey.100',
                            }
                          }} 
                          inputProps={{ min: 0, style: { textAlign: 'center', fontSize: '0.875rem' } }} 
                        />
                        <IconButton 
                          size="small" 
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          onClick={(e) => { e.stopPropagation(); updateDetalleCantidad(p, 1); }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}

        {/* Paginación en la esquina inferior */}
        {productosTotalPages > 1 && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center', 
              gap: 2, 
              mt: 3,
              pr: 2
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Página {productosPage + 1} de {productosTotalPages}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={() => setProductosPage(prev => Math.max(0, prev - 1))}
                disabled={productosPage === 0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  '&:disabled': {
                    borderColor: 'grey.200',
                    opacity: 0.5
                  }
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setProductosPage(prev => Math.min(productosTotalPages - 1, prev + 1))}
                disabled={productosPage >= productosTotalPages - 1}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.300',
                  '&:disabled': {
                    borderColor: 'grey.200',
                    opacity: 0.5
                  }
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
        </Box>
        )}

        {/* Resumen del Pedido */}
        {currentTab === RESUMEN_TAB_INDEX && (
        <Box sx={{ p: 2 }}>
          {hideFinancials ? (
            <Box>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={7}>
                  <Typography variant="caption" color="text.secondary">Cliente:</Typography>
                </Grid>
                <Grid item xs={5} textAlign="right">
                  <Typography variant="caption">{document.nroCliente || '-'}</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="caption" color="text.secondary">Nombre del Cliente:</Typography>
                </Grid>
                <Grid item xs={5} textAlign="right">
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{document.nombre || '-'}</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="caption" color="text.secondary">Fecha de Emisión:</Typography>
                </Grid>
                <Grid item xs={5} textAlign="right">
                  <Typography variant="caption">{document.fechaEmision?.toLocaleDateString?.() || '-'}</Typography>
                </Grid>
              </Grid>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Artículos ({document.detalles.length})
              </Typography>
              <Box display="grid" gap={1}>
                {document.detalles.map((d, idx) => (
                  <Box
                    key={`${d.codigoArticulo}-${idx}`}
                    onClick={() => {
                      if (!readOnly) {
                        void openProductDialogFromDetalle(d);
                      }
                    }}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      bgcolor: 'grey.50',
                      cursor: readOnly ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': readOnly
                        ? undefined
                        : {
                            bgcolor: 'grey.100',
                            borderColor: 'primary.main',
                            boxShadow: 2,
                          },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.descripcionArticulo || '-'}</Typography>
                    <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
                      <Grid item xs={7}><Typography variant="caption">Cantidad: {d.cantidad || 0}</Typography></Grid>
                      <Grid item xs={5} textAlign="right"><Typography variant="caption">Código: {d.codigoArticulo || '-'}</Typography></Grid>
                    </Grid>
                    <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Notas del renglón"
                        value={d.notas ?? ''}
                        onChange={(e) => {
                          const v = e.target.value.slice(0, VENTA_ITEM_NOTAS_MAX);
                          const detalles = [...document.detalles];
                          detalles[idx] = { ...detalles[idx], notas: v };
                          onChange({ ...document, detalles });
                        }}
                        multiline
                        minRows={1}
                        inputProps={{ maxLength: VENTA_ITEM_NOTAS_MAX }}
                        InputProps={{ readOnly }}
                        helperText={`${(d.notas ?? '').length}/${VENTA_ITEM_NOTAS_MAX}`}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
          <Box>
            <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={7}>
              <Typography variant="caption" color="text.secondary">Cliente:</Typography>
            </Grid>
            <Grid item xs={5} textAlign="right">
              <Typography variant="caption">{document.nroCliente || '-'}</Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography variant="caption" color="text.secondary">Nombre del Cliente:</Typography>
            </Grid>
            <Grid item xs={5} textAlign="right">
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{document.nombre || '-'}</Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography variant="caption" color="text.secondary">Fecha de Emisión:</Typography>
            </Grid>
            <Grid item xs={5} textAlign="right">
              <Typography variant="caption">{document.fechaEmision?.toLocaleDateString?.() || '-'}</Typography>
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Artículos ({document.detalles.length})
          </Typography>
          <Box display="grid" gap={1}>
            {document.detalles.map((d, idx) => {
              const cantidad = d.cantidad || 0;
              const precioUnit = d.precioUnitario || 0;
              const desc = Number(d.descuento || 0);
              // Subtotal Bruto = cantidad * precioUnit * (1 - descuento/100)
              const subBruto = cantidad * precioUnit * (1 - (desc / 100));
              // Subtotal Final = cantidad * precioUnit * (1 - descuento/100) * (1 + IVA) * (1 - bonif1/100) * (1 - bonif2/100) * (1 - bonif3/100)
              // Es decir: precio unitario con descuento, luego aplicar IVA, luego aplicar bonificaciones del cliente, luego multiplicar por cantidad
              const ivaMultiplier = getIvaMultiplierByCodigo(d.codigoArticulo || '');
              const precioUnitConDescuento = precioUnit * (1 - (desc / 100));
              const precioUnitConDescuentoYIVA = precioUnitConDescuento * ivaMultiplier;
              // Aplicar bonificaciones del cliente
              const bonifMultiplier = (1 - (bonif1 || 0) / 100) * (1 - (bonif2 || 0) / 100) * (1 - (bonif3 || 0) / 100);
              const subFinal = cantidad * precioUnitConDescuentoYIVA * bonifMultiplier;
              return (
                <Box 
                  key={`${d.codigoArticulo}-${idx}`} 
                  onClick={() => openProductDialogFromDetalle(d)}
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    border: '1px solid', 
                    borderColor: 'grey.200', 
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>#{idx + 1}</Typography>
                    {!readOnly && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const detalles = [...document.detalles];
                          detalles.splice(idx, 1);
                          const importeTotal = detalles.reduce((sum, det) => sum + (det.subtotal || 0), 0);
                          onChange({
                            ...document,
                            detalles,
                            importeTotal,
                          });
                        }}
                        sx={{
                          color: 'error.main',
                          padding: 0.5,
                          '&:hover': {
                            bgcolor: 'error.light',
                            color: 'error.dark',
                          },
                        }}
                        title={eliminarLabel}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.descripcionArticulo}</Typography>
                  <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={7}><Typography variant="caption">Cantidad: {cantidad}</Typography></Grid>
                    <Grid item xs={5} textAlign="right"><Typography variant="caption">Precio de Lista: {formatCurrency(precioUnit)}</Typography></Grid>
                    <Grid item xs={7}><Typography variant="caption">Código: {d.codigoArticulo}</Typography></Grid>
                    <Grid item xs={5} textAlign="right"><Typography variant="caption">Precio de Lista Final: {formatCurrency(precioUnit * ivaMultiplier)}</Typography></Grid>
                    {desc > 0 && (
                      <Grid item xs={12}>
                        <Box 
                          sx={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'success.main',
                            bgcolor: 'rgba(76, 175, 80, 0.1)'
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700 }}>
                            Descuento: {desc}%
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Notas del renglón"
                      value={d.notas ?? ''}
                      onChange={(e) => {
                        const v = e.target.value.slice(0, VENTA_ITEM_NOTAS_MAX);
                        const detalles = [...document.detalles];
                        detalles[idx] = { ...detalles[idx], notas: v };
                        onChange({ ...document, detalles });
                      }}
                      multiline
                      minRows={1}
                      inputProps={{ maxLength: VENTA_ITEM_NOTAS_MAX }}
                      InputProps={{ readOnly }}
                      helperText={`${(d.notas ?? '').length}/${VENTA_ITEM_NOTAS_MAX}`}
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="body2">Subtotal Bruto:</Typography>
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>{formatCurrency(subBruto)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'warning.dark', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Subtotal Final:
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDetalleForInfo(d);
                          setSubtotalInfoDialogOpen(true);
                        }}
                        sx={{
                          color: 'warning.dark',
                          padding: 0.25,
                          '&:hover': {
                            bgcolor: 'warning.light',
                            color: 'warning.dark',
                          },
                        }}
                        title="Ver detalles del cálculo"
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 700 }}>{formatCurrency(subFinal)}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 2, mb: 1 }}>
            Totales
          </Typography>
          <Card
            sx={{
              mt: 2,
              boxShadow: 2,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {(() => {
                const sumaSubBruto = document.detalles.reduce((sum, d) => {
                  const sb = (d.cantidad || 0) * (d.precioUnitario || 0) * (1 - Number(d.descuento || 0) / 100);
                  return sum + sb;
                }, 0);
                const _sumaSubFinal = document.detalles.reduce((sum, d) => {
                  const sb = (d.cantidad || 0) * (d.precioUnitario || 0);
                  const ivaMultiplier = getIvaMultiplierByCodigo(d.codigoArticulo || '');
                  const sf = sb * (1 - Number(d.descuento || 0) / 100) * ivaMultiplier;
                  return sum + sf;
                }, 0);
                // Calcular Subtotal Final sin IVA para aplicar bonificaciones correctamente
                const sumaSubFinalSinIVA = document.detalles.reduce((sum, d) => {
                  const sb = (d.cantidad || 0) * (d.precioUnitario || 0);
                  const sf = sb * (1 - Number(d.descuento || 0) / 100);
                  return sum + sf;
                }, 0);
                
                // Aplicar bonificaciones sobre el subtotal sin IVA
                const totalConBonif =
                  sumaSubFinalSinIVA *
                  (1 - (bonif1 || 0) / 100) *
                  (1 - (bonif2 || 0) / 100) *
                  (1 - (bonif3 || 0) / 100);
                const totalBonificacion = sumaSubFinalSinIVA - totalConBonif;
                
                // Calcular Subtotal Neto (Subtotal Bruto - Bonificaciones)
                const subtotalNeto = sumaSubBruto - totalBonificacion;
                
                // Aplicar bonificaciones al total con descuentos pero sin IVA
                const totalConBonifYDescuentos = sumaSubFinalSinIVA *
                  (1 - (bonif1 || 0) / 100) *
                  (1 - (bonif2 || 0) / 100) *
                  (1 - (bonif3 || 0) / 100);
                
                // Agrupar detalles por tasa de IVA y calcular impuestos por grupo
                const impuestosPorTasa = new Map<number, { tasa: number; monto: number }>();
                
                document.detalles.forEach((d) => {
                  const sb = (d.cantidad || 0) * (d.precioUnitario || 0);
                  const subConDescuento = sb * (1 - Number(d.descuento || 0) / 100);
                  
                  // Calcular la proporción de este detalle en el total sin IVA
                  const proporcion = sumaSubFinalSinIVA > 0 ? subConDescuento / sumaSubFinalSinIVA : 0;
                  
                  // Aplicar bonificaciones proporcionalmente
                  const subConBonif = totalConBonifYDescuentos * proporcion;
                  
                  // Obtener la tasa de IVA
                  const ivaRate = getIvaRateByCodigo(d.codigoArticulo || '');
                  const tasaIVA = ivaRate * 100; // Convertir a porcentaje
                  
                  // Calcular el monto de IVA para este detalle
                  const montoIVA = subConBonif * ivaRate;
                  
                  // Agrupar por tasa de IVA
                  if (impuestosPorTasa.has(tasaIVA)) {
                    const existente = impuestosPorTasa.get(tasaIVA)!;
                    existente.monto += montoIVA;
                  } else {
                    impuestosPorTasa.set(tasaIVA, { tasa: tasaIVA, monto: montoIVA });
                  }
                });
                
                // Convertir el Map a un array ordenado por tasa
                const impuestosArray = Array.from(impuestosPorTasa.values())
                  .filter(imp => imp.tasa > 0) // Solo mostrar impuestos con tasa > 0
                  .sort((a, b) => b.tasa - a.tasa); // Ordenar de mayor a menor
                
                // Calcular el total de IVA
                const montoIVA = impuestosArray.reduce((sum, imp) => sum + imp.monto, 0);
                const totalFinal = totalConBonifYDescuentos + montoIVA;

                return (
                  <>
                    {/* Subtotales generales */}
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Subtotal Bruto:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'primary.main', fontWeight: 700 }}
                        >
                          {formatCurrency(sumaSubBruto)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Bonificaciones compuestas (3 casilleros) */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'success.light',
                        bgcolor: 'rgba(76, 175, 80, 0.02)',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: 'success.main',
                          fontWeight: 800,
                        }}
                      >
                        Bonificación Aplicada
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          type="number"
                          value={bonif1}
                          onChange={(e) => handleBonificacionChange(1, e.target.value)}
                          sx={{ width: 80 }}
                          inputProps={{ min: 0, max: 100 }}
                        />
                        <Typography variant="body2">%</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={bonif2}
                          onChange={(e) => handleBonificacionChange(2, e.target.value)}
                          sx={{ width: 80 }}
                          inputProps={{ min: 0, max: 100 }}
                        />
                        <Typography variant="body2">%</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={bonif3}
                          onChange={(e) => handleBonificacionChange(3, e.target.value)}
                          sx={{ width: 80 }}
                          inputProps={{ min: 0, max: 100 }}
                        />
                        <Typography variant="body2">%</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography
                          variant="body2"
                          sx={{ color: 'success.main', fontWeight: 700 }}
                        >
                          Total Bonificación Aplicada:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'success.main', fontWeight: 800 }}
                        >
                          {formatCurrency(totalBonificacion)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Subtotal Neto */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Subtotal Neto:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: 'primary.main', fontWeight: 700 }}
                        >
                          {formatCurrency(subtotalNeto)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Impuestos */}
                    {impuestosArray.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'info.light',
                          bgcolor: 'rgba(33, 150, 243, 0.02)',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            color: 'info.main',
                            fontWeight: 800,
                          }}
                        >
                          Impuestos
                        </Typography>
                        {impuestosArray.map((imp, index) => (
                          <Box 
                            key={index}
                            display="flex" 
                            justifyContent="space-between"
                            sx={{ mb: index < impuestosArray.length - 1 ? 0.5 : 0 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: 'info.main', fontWeight: 700 }}
                            >
                              IVA {imp.tasa % 1 === 0 ? imp.tasa.toFixed(0) : imp.tasa.toFixed(1)}%:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: 'info.main', fontWeight: 800 }}
                            >
                              {formatCurrency(imp.monto)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Totales finales similares a la app móvil */}
                    <Box sx={{ mt: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 900, color: 'warning.dark' }}
                        >
                          Total Final:
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 900, color: 'warning.dark' }}
                        >
                          {formatCurrency(totalFinal)}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </CardContent>
          </Card>
          </Box>
          )}
        </Box>
        )}
      </Card>

      <Dialog
        open={productDialogOpen}
        onClose={closeProductDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ py: 1.5, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">Detalle de producto</Typography>
          {selectedProduct && (() => {
            const codigo = selectedProduct.codigoArticulo || selectedProduct.codigo;
            const detalleExiste = document.detalles.find(d => d.codigoArticulo === codigo);
            if (detalleExiste) {
              return (
                <IconButton
                  onClick={handleEliminarProducto}
                  size="small"
                  sx={{
                    color: 'error.main',
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: 'error.dark',
                    },
                  }}
                  title={eliminarLabel}
                >
                  <DeleteIcon />
                </IconButton>
              );
            }
            return null;
          })()}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {selectedProduct && (
            <>
              <Box display="flex" gap={2} alignItems="flex-start" mb={2}>
                {(() => {
                  const _codigo = selectedProduct.codigoArticulo || selectedProduct.codigo;
                  const desc = selectedProduct.descripcion || selectedProduct.descripcionArticulo || '';
                  const src = getProductoImageBySize(selectedProduct, 'full');
                  return (
                    <Box sx={{ width: 120, height: 120, bgcolor: 'grey.100', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <CardMedia 
                        component="img" 
                        src={src} 
                        alt={desc} 
                        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={(e: any) => {
                          e.currentTarget.src = getProductoFallbackImage();
                        }}
                      />
                    </Box>
                  );
                })()}
                <Box flex={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, textTransform: 'uppercase' }}>
                    {selectedProduct.descripcion || selectedProduct.descripcionArticulo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Código: {selectedProduct.codigoArticulo || selectedProduct.codigo}</Typography>
                  {!hideFinancials && (
                  <Box mt={1}>
                    {(() => {
                      const {
                        precioBase: listaBase,
                        precio: precioConCotizacion,
                        tieneCotizacion: tieneCot,
                        codigoMoneda,
                        productoParaMoneda,
                      } = resolverPrecioCatalogo(selectedProduct);
                      const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
                      
                      if (esMonedaEspecial) {
                        const precioLista = listaBase;
                        const precioARS = precioConCotizacion;
                        const precioFinalARS = tieneCot ? precioARS : null;
                        return (
                          <>
                            <Box display="flex" alignItems="center" gap={0.75}>
                              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                Precio de Lista: {formatearPrecioPorTipoMoneda(precioLista, productoParaMoneda, false, 5)}
                                {tieneCot && precioARS > 0 && (
                                  <span style={{ marginLeft: '8px', color: 'text.secondary', fontWeight: 400 }}>
                                    ({formatCurrency(precioARS)})
                                  </span>
                                )}
                              </Typography>
                              <Box
                                component="span"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMonedaInfoDialogOpen(true);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  width: 20,
                                  height: 20,
                                }}
                                title="Ver información de moneda"
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <text x="3" y="15" fontSize="10" fill="#424242" fontWeight="bold">$</text>
                                  <text x="17" y="15" fontSize="10" fill="#424242" fontWeight="bold">¥</text>
                                  <path d="M 7 5 Q 12 3 17 5" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 17 19 Q 12 21 7 19" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                                  <path d="M 15 4 L 17 5 L 15 6" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M 9 20 L 7 19 L 9 18" stroke="#4caf50" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </Box>
                            </Box>
                            {tieneCot ? (() => {
                              const ivaMultiplier = getIvaMultiplier(selectedProduct);
                              // Calcular precio final sin redondear hasta el final: precio * cotización * IVA
                              const precioFinalConIva = (precioFinalARS as number) * ivaMultiplier;
                              return (
                                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700 }}>
                                  Precio de Lista Final: {formatCurrency(round2(precioFinalConIva))}
                                </Typography>
                              );
                            })() : (
                              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700, mb: 0.5 }}>
                                Precio de Lista Final: N/A (Cotización no disponible)
                              </Typography>
                            )}
                          </>
                        );
                      }
                      // Precio de Lista Final = Precio de Lista + IVA (sin aplicar descuento)
                      const ivaMultiplier = getIvaMultiplier(selectedProduct);
                      const precioListaFinal = listaBase * ivaMultiplier;
                      return (
                        <>
                          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                            Precio de Lista: {formatearPrecioPorTipoMoneda(listaBase, productoParaMoneda, false)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700 }}>
                            Precio de Lista Final: {formatCurrency(precioListaFinal)}
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                  )}
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {(() => {
                      const codProd = selectedProduct.codigoArticulo || selectedProduct.codigo;
                      const stockTotal = selectedProduct.cantidad ?? selectedProduct.stock ?? selectedProduct.stockTotal ?? selectedProduct.existencia ?? '-';
                      const nTotal = typeof stockTotal === 'number' ? stockTotal : Number(stockTotal);
                      const stockEnUbic = codProd && stockPorUbicacion[codProd];
                      return (
                        <>
                          <Chip label={`Cant.Total: ${stockTotal}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codProd, selectedProduct.descripcion || selectedProduct.descripcionArticulo || '', selectedProduct.cantidad ?? selectedProduct.stock ?? selectedProduct.stockTotal ?? selectedProduct.existencia); }} sx={{ height: 22, bgcolor: 'white', borderColor: !Number.isNaN(nTotal) && nTotal > 0 ? 'success.main' : 'error.main', color: !Number.isNaN(nTotal) && nTotal > 0 ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                          {document.ubicacion && codProd && stockPorUbicacion[codProd] !== undefined && (
                            <Chip label={`Cant.Deposito: ${stockPorUbicacion[codProd]}`} size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); handleStockInfoClick(e, codProd, selectedProduct.descripcion || selectedProduct.descripcionArticulo || '', selectedProduct.cantidad ?? selectedProduct.stock ?? selectedProduct.stockTotal ?? selectedProduct.existencia); }} sx={{ height: 22, bgcolor: 'white', borderColor: (typeof stockEnUbic === 'number' && stockEnUbic > 0) ? 'success.main' : 'error.main', color: (typeof stockEnUbic === 'number' && stockEnUbic > 0) ? 'success.main' : 'error.main', cursor: 'pointer' }} />
                          )}
                        </>
                      );
                    })()}
                    {selectedProduct.codigoArticulo || selectedProduct.codigo ? (
                      <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleStockInfoClick(e, selectedProduct.codigoArticulo || selectedProduct.codigo, selectedProduct.descripcion || selectedProduct.descripcionArticulo || '', selectedProduct.cantidad ?? selectedProduct.stock ?? selectedProduct.stockTotal ?? selectedProduct.existencia)} aria-label="Ver stock por ubicación">
                        <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />
                      </IconButton>
                    ) : null}
                  </Stack>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                    Categoría: {(() => {
                      const cod = selectedProduct.codigoRamo;
                      return ramoCache[String(cod)] || String(cod ?? '-');
                    })()}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              {(() => {
                const codigoMoneda = getCodigoMonedaProducto(selectedProduct);
                const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
                const noHayCotizacionParaEstaMoneda = Boolean(esMonedaEspecial && codigoMoneda !== null && codigoMoneda !== 0 && noHayCotizacionDisponible(codigoMoneda));
                
                return (
                  <>
                    <Box display="grid" gridTemplateColumns="1fr" justifyItems="center" mt={1}>
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Cantidad:</Typography>
                      <Box display="flex" alignItems="center" gap={2}>
                        <IconButton 
                          onClick={() => setDialogQty(q => Math.max(0, (q || 0) - 1))}
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          sx={{ 
                            bgcolor: (!clienteSeleccionado || noHayCotizacionParaEstaMoneda) ? 'grey.300' : 'primary.main', 
                            color: 'white', 
                            '&:hover': { bgcolor: (!clienteSeleccionado || noHayCotizacionParaEstaMoneda) ? 'grey.300' : 'primary.dark' },
                            width: 36, 
                            height: 36, 
                            borderRadius: '50%',
                            '&.Mui-disabled': {
                              bgcolor: 'grey.300',
                              color: 'grey.500',
                            }
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField
                          size="small"
                          type="number"
                          value={dialogQty}
                          onChange={(e) => setDialogQty(Math.max(0, Number(e.target.value) || 0))}
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          sx={{ 
                            width: 90, 
                            '& .MuiInputBase-input': { 
                              textAlign: 'center', 
                              fontWeight: 700,
                              color: (!clienteSeleccionado || noHayCotizacionParaEstaMoneda) ? 'text.disabled' : 'text.primary',
                            },
                            '& .MuiInputBase-root.Mui-disabled': {
                              bgcolor: 'grey.100',
                            }
                          }}
                          inputProps={{ min: 0 }}
                        />
                        <IconButton 
                          onClick={() => setDialogQty(q => (q || 0) + 1)}
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          sx={{ 
                            bgcolor: (!clienteSeleccionado || noHayCotizacionParaEstaMoneda) ? 'grey.300' : 'primary.main', 
                            color: 'white', 
                            '&:hover': { bgcolor: (!clienteSeleccionado || noHayCotizacionParaEstaMoneda) ? 'grey.300' : 'primary.dark' },
                            width: 36, 
                            height: 36, 
                            borderRadius: '50%',
                            '&.Mui-disabled': {
                              bgcolor: 'grey.300',
                              color: 'grey.500',
                            }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Campo para editar precio de lista si el ramo permite editar precio */}
                    {!hideFinancials && (() => {
                      const codigoRamo = selectedProduct?.codigoRamo;
                      const puedeEditarPrecio = codigoRamo ? ramoEditaPrecioCache[codigoRamo] : false;
                      
                      if (puedeEditarPrecio) {
                        return (
                          <Box display="flex" alignItems="center" gap={1.5} mt={2}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Precio de Lista:</Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={dialogPrecioLista ?? ''}
                              onChange={(e) => {
                                const nuevoPrecio = Number(e.target.value);
                                if (!isNaN(nuevoPrecio) && nuevoPrecio >= 0) {
                                  setDialogPrecioLista(nuevoPrecio);
                                } else if (e.target.value === '') {
                                  setDialogPrecioLista(null);
                                }
                              }}
                              disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                              sx={{ 
                                width: 140, 
                                '& .MuiInputBase-input': { 
                                  textAlign: 'right',
                                  color: (!clienteSeleccionado || noHayCotizacionParaEstaMoneda) ? 'text.disabled' : 'text.primary',
                                },
                                '& .MuiInputBase-root.Mui-disabled': {
                                  bgcolor: 'grey.100',
                                }
                              }}
                              inputProps={{ min: 0, step: 0.01 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                            />
                          </Box>
                        );
                      }
                      return null;
                    })()}
                    {!hideFinancials && (
                      <Box display="flex" alignItems="center" gap={1.5} mt={2}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>Descuento (%):</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={dialogDiscount}
                          onChange={(e) => setDialogDiscount(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                          disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda}
                          sx={{
                            width: 140,
                            '& .MuiInputBase-input': {
                              textAlign: 'right',
                              color: (!clienteSeleccionado || noHayCotizacionParaEstaMoneda) ? 'text.disabled' : 'text.primary',
                            },
                            '& .MuiInputBase-root.Mui-disabled': {
                              bgcolor: 'grey.100',
                            }
                          }}
                          inputProps={{ min: 0, max: 100 }}
                        />
                      </Box>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      label="Notas del renglón"
                      value={dialogNotas}
                      onChange={(e) => setDialogNotas(e.target.value.slice(0, VENTA_ITEM_NOTAS_MAX))}
                      disabled={!clienteSeleccionado || noHayCotizacionParaEstaMoneda || readOnly}
                      multiline
                      minRows={2}
                      sx={{ mt: 2 }}
                      inputProps={{ maxLength: VENTA_ITEM_NOTAS_MAX }}
                      helperText={`${dialogNotas.length}/${VENTA_ITEM_NOTAS_MAX}`}
                    />
                  </>
                );
              })()}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeProductDialog} variant="outlined" color="inherit" sx={{ borderRadius: 999, px: 3 }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleActualizarProducto} 
            variant="contained" 
            sx={{ borderRadius: 999, px: 3 }}
            disabled={Boolean(!clienteSeleccionado || (() => {
              const codigoMoneda = getCodigoMonedaProducto(selectedProduct);
              return codigoMoneda && codigoMoneda !== 0 && noHayCotizacionDisponible(codigoMoneda);
            })())}
          >
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>
      

      {/* Botón Guardar solo se muestra en la última ventana (summary) y cuando no está en modo solo lectura */}
      {currentTab === RESUMEN_TAB_INDEX && !readOnly && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={loading || !document.nroCliente}
            fullWidth
            sx={{ maxWidth: 480, borderRadius: 999 }}
          >
            {loading ? 'Guardando...' : saveButtonText}
          </Button>
        </Box>
      )}

      {/* Diálogo de Forma de Pago */}
      <Dialog
        open={formaDePagoDialogOpen}
        onClose={() => setFormaDePagoDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Seleccionar Forma de Pago
            </Typography>
            <IconButton
              size="small"
              onClick={() => setFormaDePagoDialogOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <RadioGroup
            value={document.formaDePago || ''}
            onChange={(e) => {
              onChange({
                ...document,
                formaDePago: e.target.value,
              } as FormularioVentaFormDocument);
              setFormaDePagoDialogOpen(false);
            }}
          >
            {formasDePago.map((forma) => {
              const isSelected = document.formaDePago === forma;
              return (
                <Box
                  key={forma}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: isSelected ? 'rgba(156, 39, 176, 0.1)' : 'transparent',
                    border: isSelected ? '1px solid rgba(156, 39, 176, 0.3)' : 'none',
                    '&:hover': {
                      bgcolor: isSelected ? 'rgba(156, 39, 176, 0.15)' : 'action.hover',
                    },
                  }}
                >
                  <FormControlLabel
                    value={forma}
                    control={
                      <Radio
                        sx={{
                          color: isSelected ? '#9c27b0' : 'text.secondary',
                          '&.Mui-checked': {
                            color: '#9c27b0',
                          },
                        }}
                      />
                    }
                    label={forma}
                    sx={{
                      width: '100%',
                      m: 0,
                      px: 2,
                      py: 1,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '1rem',
                        fontWeight: isSelected ? 500 : 400,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </RadioGroup>
        </DialogContent>
      </Dialog>

      {/* Diálogo de Ubicación */}
      <Dialog
        open={ubicacionDialogOpen}
        onClose={() => {
          setUbicacionDialogOpen(false);
          setUbicacionSearchTerm('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Seleccionar Ubicación
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setUbicacionDialogOpen(false);
                setUbicacionSearchTerm('');
              }}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Campo de búsqueda */}
          <TextField
            fullWidth
            placeholder="Buscar ubicación..."
            value={ubicacionSearchTerm}
            onChange={(e) => setUbicacionSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          {/* Lista de ubicaciones con RadioButtons */}
          <RadioGroup
            value={document.ubicacion?.match(/\((\d+)\)/)?.[1] || ''}
            onChange={(e) => {
              const selectedUbicacion = ubicaciones.find(
                (u) => String(u.nroUbicacion) === e.target.value
              );
              if (selectedUbicacion) {
                onChange({
                  ...document,
                  ubicacion: `${selectedUbicacion.nombreUbicacion} (${selectedUbicacion.nroUbicacion})`,
                } as FormularioVentaFormDocument);
                setUbicacionDialogOpen(false);
                setUbicacionSearchTerm('');
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filteredUbicaciones.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No hay ubicaciones disponibles
                </Typography>
              ) : (
                filteredUbicaciones.map((ubicacion) => {
                  const ubicacionValue = `${ubicacion.nombreUbicacion} (${ubicacion.nroUbicacion})`;
                  const isSelected = document.ubicacion === ubicacionValue;
                  return (
                    <Box
                      key={ubicacion.nroUbicacion}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <FormControlLabel
                        value={String(ubicacion.nroUbicacion)}
                        control={
                          <Radio
                            sx={{
                              color: 'text.secondary',
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        }
                        label={ubicacionValue}
                        sx={{
                          width: '100%',
                          m: 0,
                          px: 2,
                          py: 1,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '1rem',
                            fontWeight: isSelected ? 500 : 400,
                          },
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setUbicacionDialogOpen(false);
              setUbicacionSearchTerm('');
            }} 
            variant="outlined"
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Información de Moneda */}
      <Dialog
        open={monedaInfoDialogOpen}
        onClose={() => setMonedaInfoDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
            Información de Moneda
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedProduct && (() => {
            const {
              precioBase: precioOriginal,
              codigoMoneda,
              productoParaMoneda,
            } = resolverPrecioCatalogo(selectedProduct);
            const tipoMoneda = productoParaMoneda.tipoMoneda ?? productoParaMoneda.tipoMonedaArticulo ?? 0;
            const esMonedaEspecial = codigoMoneda !== null && codigoMoneda !== 0;
            const monedaOriginal = esMonedaEspecial 
              ? getNombreMoneda(codigoMoneda, Number(tipoMoneda))
              : (productoParaMoneda.monedaOriginal ?? productoParaMoneda.codigoMonedaOriginal ?? 'ARS');
            const cotizacion = esMonedaEspecial ? getCotizacionPorProducto(productoParaMoneda) : (productoParaMoneda.cotizacion ?? productoParaMoneda.cotizacionAplicada ?? null);
            const cotizacionDisponible = cotizacion !== null && cotizacion !== undefined && cotizacion > 0;
            const noHayCotizacionParaEstaMoneda = Boolean(esMonedaEspecial && codigoMoneda && codigoMoneda !== 0 && noHayCotizacionDisponible(codigoMoneda));
            
            // Formatear precio: 5 decimales para monedas extranjeras, 2 para ARS
            const formatearPrecioMoneda = (precio: number, esMonedaExtranjera: boolean = false) => {
              const decimales = esMonedaExtranjera ? 5 : 2;
              return precio.toFixed(decimales);
            };
            
            return (
              <>
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Precio Mon. Original:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {monedaOriginal} {formatearPrecioMoneda(precioOriginal, esMonedaEspecial)}
                    </Typography>
                  </Box>
                  {esMonedaEspecial && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Cotización Aplicada:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {cotizacionDisponible ? formatearPrecioMoneda(cotizacion) : 'No disponible'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {noHayCotizacionParaEstaMoneda && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'error.main',
                      border: '1px solid',
                      borderColor: 'error.dark',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                      ⚠️ Cotización no disponible
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', fontSize: '0.875rem' }}>
                      No hay ninguna cotización cargada para {monedaOriginal}. El precio se mantendrá en {monedaOriginal} ({monedaOriginal} {formatearPrecioMoneda(precioOriginal, esMonedaEspecial)}).
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setMonedaInfoDialogOpen(false)} 
            variant="contained" 
            color="primary"
            fullWidth
            sx={{ borderRadius: 999, px: 3, textTransform: 'uppercase', fontWeight: 700 }}
          >
            CERRAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Información del Subtotal Final */}
      <Dialog
        open={subtotalInfoDialogOpen}
        onClose={() => setSubtotalInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
            Detalle de Importes del Producto
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedDetalleForInfo && (() => {
            const cantidad = selectedDetalleForInfo.cantidad || 0;
            const precioUnit = selectedDetalleForInfo.precioUnitario || 0;
            const desc = Number(selectedDetalleForInfo.descuento || 0);
            const codigoArticulo = selectedDetalleForInfo.codigoArticulo || '';
            const _producto = productosDataCache[codigoArticulo];
            const _ivaMultiplier = getIvaMultiplierByCodigo(codigoArticulo);
            const ivaRate = getIvaRateByCodigo(codigoArticulo);
            const _ivaPorcentaje = (ivaRate * 100).toFixed(2);
            
            // Cálculos paso a paso
            const subtotalSinDescuento = cantidad * precioUnit;
            const montoDescuento = subtotalSinDescuento * (desc / 100);
            const subtotalBruto = subtotalSinDescuento - montoDescuento;
            const montoIVA = subtotalBruto * ivaRate;
            const subtotalConIVA = subtotalBruto + montoIVA;
            // Aplicar bonificaciones del cliente (se aplican secuencialmente)
            const bonifMultiplier = (1 - (bonif1 || 0) / 100) * (1 - (bonif2 || 0) / 100) * (1 - (bonif3 || 0) / 100);
            // Calcular montos de cada bonificación
            const montoBonif1 = subtotalConIVA * ((bonif1 || 0) / 100);
            const subtotalDespuesBonif1 = subtotalConIVA * (1 - (bonif1 || 0) / 100);
            const montoBonif2 = subtotalDespuesBonif1 * ((bonif2 || 0) / 100);
            const subtotalDespuesBonif2 = subtotalDespuesBonif1 * (1 - (bonif2 || 0) / 100);
            const montoBonif3 = subtotalDespuesBonif2 * ((bonif3 || 0) / 100);
            const _totalBonificacion = montoBonif1 + montoBonif2 + montoBonif3;
            const subtotalFinal = subtotalConIVA * bonifMultiplier;
            // Bonificación aplicada sin impuestos
            const bonificacionAplicadaSinImpuestos = (subtotalConIVA - subtotalFinal) / (1 + ivaRate);
            // Subtotal Neto = Subtotal Bruto - Bonificación Aplicada (sin impuestos)
            const subtotalNeto = subtotalBruto - bonificacionAplicadaSinImpuestos;
            
            return (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {selectedDetalleForInfo.descripcionArticulo}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Cantidad:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{cantidad}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Precio de Lista:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(precioUnit)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal Bruto del Artículo:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(subtotalSinDescuento)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'success.main' }}>% Descuento del Artículo:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>{desc}%</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal Bruto:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(subtotalBruto)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      Bonificación Aplicada
                      {((bonif1 > 0 || bonif2 > 0 || bonif3 > 0) && (
                        <> ({[bonif1, bonif2, bonif3].filter(b => b > 0).join('%+')}%)</>
                      ))}
                      :
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {formatCurrency(bonificacionAplicadaSinImpuestos)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal Neto:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(subtotalNeto)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" sx={{ fontWeight: 800, color: 'warning.dark' }}>
                      Subtotal Final:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: 'warning.dark' }}>
                      {formatCurrency(subtotalFinal)}
                    </Typography>
                  </Box>
                </Box>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setSubtotalInfoDialogOpen(false)} 
            variant="contained" 
            color="primary"
            fullWidth
            sx={{ borderRadius: 999, px: 3, textTransform: 'uppercase', fontWeight: 700 }}
          >
            CERRAR
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(stockInfoAnchorEl)}
        anchorEl={stockInfoAnchorEl}
        onClose={handleStockInfoClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 2, minWidth: 320, maxWidth: 480 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Stock por ubicación
            {stockInfoProducto && (
              <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {stockInfoProducto.codigoArticulo} - {stockInfoProducto.descripcion}
              </Typography>
            )}
          </Typography>
          {stockInfoLoading ? (
            <Typography variant="body2" color="text.secondary">Cargando...</Typography>
          ) : stockInfoData.length === 0 && (stockInfoProducto?.stockTotal === undefined || stockInfoProducto?.stockTotal === null) ? (
            <Typography variant="body2" color="text.secondary">No hay datos disponibles</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ubicación</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Asignado</TableCell>
                    <TableCell align="right">Disponible</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockInfoProducto?.stockTotal !== undefined && stockInfoProducto.stockTotal !== null && (
                    <TableRow sx={(theme) => ({ bgcolor: theme.palette.grey[100], '& td': { fontWeight: 600 } })}>
                      <TableCell>TOTAL</TableCell>
                      <TableCell align="right">{Number(stockInfoProducto.stockTotal).toFixed(2)}</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">—</TableCell>
                    </TableRow>
                  )}
                  {stockInfoData.map((row) => {
                    const nroUbicacionStr = document.ubicacion?.match(/\((\d+)\)/)?.[1];
                    const nroUbicacionSeleccionada = nroUbicacionStr ? parseInt(nroUbicacionStr, 10) : null;
                    const esUbicacionSeleccionada = nroUbicacionSeleccionada !== null && row.nroUbicacion === nroUbicacionSeleccionada;
                    return (
                      <TableRow
                        key={`${row.codigoArticulo}-${row.nroUbicacion}`}
                        sx={esUbicacionSeleccionada ? (theme) => ({ bgcolor: theme.palette.action.selected, '& td': { fontWeight: 600 } }) : undefined}
                      >
                        <TableCell>{row.nombreUbicacion}</TableCell>
                        <TableCell align="right">{Number(row.cantidadDeposito ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{Number(row.cantidadAsignada ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{Number(row.disponibleDeposito ?? 0).toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Popover>

      <Dialog
        open={openPendientesMaster}
        onClose={() => setOpenPendientesMaster(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ color: 'primary.main', fontWeight: 600 }}>Buscar pendientes</DialogTitle>
        <DialogContent>
          {loadingPendientesMaster ? (
            <Typography variant="body2" color="text.secondary">
              Cargando pendientes...
            </Typography>
          ) : pendientesMasterRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {hideFinancials
                ? 'No hay líneas con cantidad pendiente de entregar para este cliente.'
                : 'No hay líneas con cantidad pendiente de facturar para este cliente.'}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 170 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 110 }}>Fecha emisión</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, width: 82 }}>
                      Cant. pedida
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={(theme) => ({
                        width: 82,
                        fontWeight: 700,
                        color: !hideFinancials ? theme.palette.primary.dark : undefined,
                        backgroundColor: !hideFinancials ? theme.palette.primary.light + '22' : undefined,
                      })}
                    >
                      Pend. facturar
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={(theme) => ({
                        width: 82,
                        fontWeight: 700,
                        color: hideFinancials ? theme.palette.success.dark : undefined,
                        backgroundColor: hideFinancials ? theme.palette.success.light + '22' : undefined,
                      })}
                    >
                      Pend. entregar
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: 132 }}>
                      Cant. a agregar
                    </TableCell>
                    <TableCell align="center" sx={{ width: 44 }} />
                    <TableCell align="center" sx={{ fontWeight: 600, width: 96 }}>
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientesMasterRows.map((p) => (
                    <TableRow key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteVentaMasterDisplay(p)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {p.fechaEmisionComprobanteMaster
                          ? new Date(p.fechaEmisionComprobanteMaster).toLocaleDateString('es-AR')
                          : ''}
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {p.codigoArticulo} - {p.descripcionArticulo}
                      </TableCell>
                      <TableCell align="right">{p.cantidadPedida}</TableCell>
                      <TableCell
                        align="right"
                        sx={(theme) => ({
                          fontWeight: !hideFinancials ? 700 : undefined,
                          color: !hideFinancials ? theme.palette.primary.dark : undefined,
                          backgroundColor: !hideFinancials ? theme.palette.primary.light + '14' : undefined,
                        })}
                      >
                        {p.pendienteFacturar}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={(theme) => ({
                          fontWeight: hideFinancials ? 700 : undefined,
                          color: hideFinancials ? theme.palette.success.dark : undefined,
                          backgroundColor: hideFinancials ? theme.palette.success.light + '14' : undefined,
                        })}
                      >
                        {p.pendienteEntregar}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                          <TextField
                            size="small"
                            type="number"
                            value={cantidadesPendientesMaster[getPendienteMasterRowKey(p)] ?? ''}
                            onChange={(e) =>
                              setCantidadesPendientesMaster((prev) => ({
                                ...prev,
                                [getPendienteMasterRowKey(p)]: e.target.value,
                              }))
                            }
                            inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                            sx={{ width: 96 }}
                          />
                          {parsePendienteMasterCantidadEditable(p) > getPendienteMasterOriginal(p) && (
                            <Tooltip title="La cantidad supera el pendiente original">
                              <WarningAmberOutlinedIcon color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver comprobantes facturados y remitidos">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPendienteHiloInfo({
                                nroFormularioMaster: p.nroFormularioMaster,
                                nroItemMaster: p.nroItemMaster,
                                resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                              })
                            }
                            aria-label="Información facturado y remitido"
                          >
                            <InfoOutlinedIcon fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            void agregarPendienteDesdeMaster(p, parsePendienteMasterCantidadEditable(p))
                          }
                        >
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void agregarTodosPendientesDesdeMaster()} disabled={pendientesMasterRows.length === 0}>
            Agregar todo
          </Button>
          <Button onClick={() => setOpenPendientesMaster(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!pendienteHiloInfo} onClose={() => setPendienteHiloInfo(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Facturado y remitido (detalle)</DialogTitle>
        <DialogContent>
          {pendienteHiloInfo?.resumenLinea ? (
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              {pendienteHiloInfo.resumenLinea}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Línea master {pendienteHiloInfo?.nroFormularioMaster}/{pendienteHiloInfo?.nroItemMaster}
          </Typography>

          {loadingPendienteHilo ? (
            <Typography variant="body2" color="text.secondary">
              Cargando detalle...
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Cantidad
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendienteHiloItems.map((c) => (
                    <TableRow key={`${c.nroFormulario}-${c.nroItem}-${c.nroTipoFormulario}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {c.fechaEmision ? new Date(c.fechaEmision).toLocaleDateString('es-AR') : ''}
                      </TableCell>
                      <TableCell>{formatComprobanteVentaHiloLine(c)}</TableCell>
                      <TableCell align="right">{c.cantidad}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendienteHiloInfo(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <VendedorSearch
        open={openVendedorSearch}
        onClose={() => setOpenVendedorSearch(false)}
        onSelect={handleSelectVendedor}
      />

      <ListaPrecioSearch
        open={openListaPrecioSearch}
        onClose={() => setOpenListaPrecioSearch(false)}
        onSelect={handleSelectListaPrecio}
      />
    </>
  );
};

export default FormularioVentaForm;